import { withTelemetry } from "../observability/web/runtime";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../utils/reduxHelper";
import { APIS_ENDPOINT } from "../utils/api_constants";
import { trimClientOgData } from "../utils/common";

function buildPayoutQueryPath(basePath, params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `${basePath}?${query}` : basePath;
}

export const reduxAPI = createApi({
  reducerPath: "reduxAPI",
  baseQuery: withTelemetry("reduxAPI", baseQueryWithArgs),
  tagTypes: [
    "Creator",
    "Posts",
    "Collections",
    "Products",
    "Drafts",
    "ShopCounts",
    "Tags",
    "Items",
    "NewPost",
    "ProductLinks",
    "ProductStatus",
    "postOrCollectionProducts",
    "YoutubeLogin",
    "FirebaseLogin",
    "authStatus",
    "ManualSocialLogin",
    "InstagramLogin",
    "PinterestLogin",
    "SnapchatLogin",
    "InstaPosts",
    "FacebookPosts",
    "YoutubePosts",
    "CreatorSocialLogin",
    "FilteredBrands",
    "brandTags",
    "filterAndSort",
    "Banners",
    "frameFilters",
    "Redirects",
    "BankDetails",
    "CTKDetails",
    "Templates",
    "SalesData",
    "AnalyticsChartData",
    "AmazonData",
    "BrandWiseEarningsSplit",
    "ChannelWiseEarningsSplit",
    "RedirectionsAnalytics",
    "LifeTimeEarnings",
    "PastPayouts",
    "Milestones",
    "Reels",
    "Usernames",
    "PostsReleaseDates",
    "RewardsAnalytics",
    "PostOrCollectionLifetimeFunnel",
    "PostOrCollectionLifetimeEarnings",
    "PostOrCollectionProductEarnings",
    "InstaBasicData",
    "ProductAnalytics",
    "Feedback",
    "AnalyticsPosts",
    "AnalyticsCollections",
    "AnalyticsProducts",
    "PostOrCollectionDetails",
    "Address",
    "ProductFinds",
    "CreatorDemo",
    "CreatorWinterMeet",
    "CreatorBPCMeet",
    "Overlays",
    "similarProductRecommendations",
    "Collage",
    "AutoContent",
    "BrandAffiliation",
    "FTUX",
    "BrandCampaign",
    "BrandCampaignRejectionPopup",
    "Thrift",
    "TotalEarnings",
    "CollabHistory",
    "ScriptSubmissions",
    "Campaign",
    "StoryBannerStatus",
    "IgStoryDrawerStatus",
    "InstaStories",
    "Folders",
    "FolderItems",
    "QuickAccessIndicators",
    "RewardsListingMeta",
    "EngageListingMeta",
    "brandSourcingCollections",
    "SetupJourney",
    "CreatorIntent",
    "EngageSetupJourney",
    "COCEvent",
    "CreatorStreaks",
    "CreatorStreakDetail",
    "CreatorStreakActivities",
    "StreaksPayoutHistory",
    "WishlinkRewardDetails",
    "IGPostSuggestion",
    "TDSCertificateSummary",
    "PanBankPoll",
  ],
  endpoints: (builder) => ({
    //GET Queries
    getCreatorData: builder.query({
      refetchOnFocus: true,
      query: () =>
        getQueryStringWithCreator(
          `${APIS_ENDPOINT.getCreatorData.getEndpoint()}`
        ),
      providesTags: ["Creator"],
      transformResponse: (res) => res.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `endpointName=${endpointName}&creatorId=${queryArgs?.creatorId}`;
      },
    }),
    getStorePostsData: builder.query({
      query: ({ page, sort, filter, limit }) =>
        getQueryStringWithCreator(
          `${APIS_ENDPOINT.getStorePostsData.getEndpoint()}?page=${page}&sort=${sort}&filter=${filter}${
            limit ? `&limit=${limit}` : ""
          }`
        ),
      providesTags: ["Posts"],
    }),
    getStoreCollectionsData: builder.query({
      query: ({ page, sort, filter }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopCollection?page=${page}&sort=${sort}&filter=${filter}`
        ),
      providesTags: ["Collections"],
    }),
    getStoreProductsData: builder.query({
      query: ({ page, sort, filter }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopProducts?page=${page}&sort=${sort}&filter=${filter}`
        ),
      providesTags: ["Products"],
    }),
    getStoreDraftsData: builder.query({
      query: ({ sort, filter }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopDrafts?sort=${sort}&filter=${filter}`
        ),
      providesTags: ["Drafts"],
    }),
    getCreatorShopCounts: builder.query({
      query: ({ sort, filter }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopCounts?sort=${sort}&filter=${filter}`
        ),
      providesTags: ["ShopCounts"],
    }),
    getPostProductsDetails: builder.query({
      query: ({ postType, postOrCollectionId, needClicks }) =>
        getQueryStringWithCreator(
          `c/getShopProductsDetails?postType=${postType}&postOrCollectionId=${postOrCollectionId}&needClicks=${needClicks}`
        ),
      providesTags: ["Products", "ProductStatus"],
    }),
    getCreatorTags: builder.query({
      query: () => getQueryStringWithCreator(`c/getShopPostTagsMasterList`),
      providesTags: ["Tags"],
    }),
    getCreatorPostData: builder.query({
      query: ({ postType, postOrCollectionId }) =>
        getQueryStringWithCreator(
          `c/getShopPostOrCollectionDetails?postType=${postType}&postOrCollectionId=${postOrCollectionId}`
        ),
      providesTags: ["PostOrCollectionDetails"],
    }),
    getProductLinksData: builder.query({
      query: ({ postType, postOrCollectionId }) =>
        getQueryStringWithCreator(
          `c/getShopProductsDetails?postType=${postType}&postOrCollectionId=${postOrCollectionId}`
        ),
      providesTags: ["ProductLinks"],
    }),
    getOosReplacements: builder.query({
      query: ({ productId }) =>
        getQueryStringWithCreator(
          `c/getOosReplacements?productId=${productId}`
        ),
    }),
    swapOosProduct: builder.mutation({
      query: ({ originalProductId, replacementProductId }) => ({
        url: getQueryStringWithCreator(`c/swapOosProduct`),
        method: "POST",
        body: {
          original_product_id: originalProductId,
          replacement_product_id: replacementProductId,
        },
      }),
      invalidatesTags: ["ProductLinks"],
    }),
    getPostOrCollectionProductsData: builder.query({
      query: ({ postType, postOrCollectionId }) =>
        getQueryStringWithCreator(
          `getPostOrCollectionProducts?postType=${postType}&postOrCollectionId=${postOrCollectionId}`
        ),
      providesTags: ["postOrCollectionProducts"],
    }),

    checkMiniSourcing: builder.query({
      query: () => getQueryStringWithCreator(`s/checkIsMiniSourcingAlive`),
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        }
        return undefined;
      },
    }),
    getSessionId: builder.query({
      query: ({ sessionId, platform }) =>
        getQueryStringWithCreator(
          `c/getSessionId?sessionId=${sessionId}&platform=${platform}`
        ),
    }),
    getInstaPostsList: builder.query({
      query: ({ nextPageCursor }) =>
        getQueryStringWithCreator(
          `c/getInstaPostsList?nextPageCursor=${nextPageCursor}`
        ),
      extraOptions: {
        refetchOnMountOrArgChange: true,
      },
      providesTags: ["InstaPosts"],
    }),
    getInstaStoriesList: builder.query({
      query: ({ nextPageCursor }) =>
        getQueryStringWithCreator(
          `c/getInstaPostsList?nextPageCursor=${nextPageCursor}&include_stories=true`
        ),
      extraOptions: {
        refetchOnMountOrArgChange: true,
      },
      providesTags: ["InstaStories"],
    }),
    getInstaPostsListExplicit: builder.query({
      query: ({ nextPageCursor }) =>
        getQueryStringWithCreator(
          `c/getInstaPostsList?nextPageCursor=${nextPageCursor}&include_stories=false`
        ),
      extraOptions: {
        refetchOnMountOrArgChange: true,
      },
      providesTags: ["InstaPosts"],
    }),
    getFacebookPostsList: builder.query({
      query: ({ nextPageCursor }) =>
        getQueryStringWithCreator(
          `c/getFacebookPostsList?nextPageCursor=${nextPageCursor}`
        ),
      extraOptions: {
        refetchOnMountOrArgChange: true,
      },
      providesTags: ["FacebookPosts"],
    }),
    getYoutubePostsList: builder.query({
      query: ({ nextPageToken }) =>
        getQueryStringWithCreator(
          `c/getYoutubePostsList?nextPageToken=${encodeURIComponent(
            nextPageToken ?? ""
          )}`
        ),
      providesTags: ["YoutubePosts"],
    }),
    getCreatorSocialLoginStatus: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorSocialLoginStatus`),
      providesTags: ["CreatorSocialLogin", "YoutubeLogin", "InstagramLogin"],
    }),
    fetchBrandTagsData: builder.query({
      query: ({ parent }) =>
        getQueryStringWithCreator(`c/fetchBrandTags?parent=${parent}`),
      providesTags: ["brandTags"],
    }),
    getFiltersAndSortData: builder.query({
      query: () =>
        getQueryStringWithCreator(`c/fetchCreatorShopFiltersAndSort`),
      providesTags: ["filterAndSort"],
    }),
    fetchAllBrandBannersData: builder.query({
      query: () => getQueryStringWithCreator(`c/fetchBrandsHomeBanners`),
      providesTags: ["Banners"],
    }),
    getFrameFiltersData: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorContentFrameFilters`),
      providesTags: ["frameFilters"],
      transformResponse: (res) => res?.data,
    }),
    getPostRedirects: builder.query({
      query: ({ page, sort, filter, startDate, endDate }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopPostOrCollectionRedirects?type=post&page=${page}&sort=${sort}&filter=${filter}&startDate=${startDate}&endDate=${endDate}`
        ),
      providesTags: ["PostRedirects"],
    }),
    getCollectionRedirects: builder.query({
      query: ({ page, sort, filter, startDate, endDate }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopPostOrCollectionRedirects?type=collection&page=${page}&sort=${sort}&filter=${filter}&startDate=${startDate}&endDate=${endDate}`
        ),
      providesTags: ["CollectionRedirects"],
    }),
    getProductRedirects: builder.query({
      query: ({ page, sort, filter, startDate, endDate }) =>
        getQueryStringWithCreator(
          `c/fetchCreatorShopPostOrCollectionRedirects?type=products&page=${page}&sort=${sort}&filter=${filter}&startDate=${startDate}&endDate=${endDate}`
        ),
      providesTags: ["ProductRedirects"],
    }),
    checkTaskStatus: builder.query({
      query: ({ task_id }) => ({
        url: getQueryStringWithCreator(`c/checkTaskStatus?task_id=${task_id}`),
        method: "GET",
      }),
    }),
    fetchCreatorBankDetails: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/fetchCreatorBankDetails`),
      }),
      providesTags: ["BankDetails"],
    }),
    getCTKDetails: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCTKDetails`),
      }),
      providesTags: ["CTKDetails"],
    }),
    getCreatorTemplates: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorTemplates`),
      }),
      providesTags: ["Templates"],
    }),
    getCreatorSalesData: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorSalesData?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["SalesData"],
    }),
    getCreatorAnalyticsChartData: builder.query({
      query: ({
        startDatePrevious,
        endDatePrevious,
        endDateToAchieve,
        chartType,
        showNextPage,
        showPreviousPage,
      }) => ({
        url: getQueryStringWithCreator(
          `c/getAnalyticsChartData?startDatePrevious=${startDatePrevious}&endDatePrevious=${endDatePrevious}&endDateToAchieve=${endDateToAchieve}&chartType=${chartType}&showNextPage=${showNextPage}&showPreviousPage=${showPreviousPage}`
        ),
      }),
      providesTags: ["AnalyticsChartData"],
    }),
    getAmazonData: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getAmazonData?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["AmazonData"],
    }),
    getBrandWiseEarningsSplitData: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getBrandWiseData?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["BrandWiseEarningsSplit"],
    }),
    getChannelWiseEarningsSplitData: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getChannelWiseData?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["ChannelWiseEarningsSplit"],
    }),
    getCreatorAnalyticsPostsData: builder.query({
      query: ({ startDate, endDate, page, sort, filters }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorAnalyticsPosts?startDate=${startDate}&endDate=${endDate}&page=${page}&sort=${sort}&filters=${filters}`
        ),
      }),
      providesTags: ["AnalyticsPosts"],
    }),
    getCreatorAnalyticsCollectionsData: builder.query({
      query: ({ startDate, endDate, page, sort, filters }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorAnalyticsCollections?startDate=${startDate}&endDate=${endDate}&page=${page}&sort=${sort}&filters=${filters}`
        ),
      }),
      providesTags: ["AnalyticsCollections"],
    }),
    getCreatorAnalyticsProductsData: builder.query({
      query: ({ startDate, endDate, page, sort, filters }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorAnalyticsProducts?startDate=${startDate}&endDate=${endDate}&page=${page}&sort=${sort}&filters=${
            filters || {}
          }`
        ),
      }),
      providesTags: ["AnalyticsProducts"],
    }),
    getCreatorAnalyticsRedirectionsData: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorRedirectionsData?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["RedirectionsAnalytics"],
    }),
    getCreatorLifetimeEarnings: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorLifetimeEarnings`),
      }),
      providesTags: ["LifeTimeEarnings"],
    }),
    // Payouts CX variant — lifetime total from PAID payouts only.
    getCreatorLifetimeEarningsV2: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorLifetimeEarningsV2`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCreatorPastPayouts: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorPastPayouts`),
      }),
      providesTags: ["PastPayouts"],
    }),
    getMarketingMediaReels: builder.query({
      query: ({ isHome }) => ({
        url: getQueryStringWithCreator(
          `c/getMarketingMediaReels?isHomepage=${isHome}`
        ),
      }),
      providesTags: ["Reels"],
    }),
    getCreatorsUsernames: builder.query({
      query: ({ creatorUsernameQuery }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorsUsernames?username=${creatorUsernameQuery}`
        ),
      }),
      providesTags: ["Usernames"],
    }),
    getCalendarPostsReleaseDates: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCalendarPostsReleaseDates`),
      }),
      providesTags: ["PostsReleaseDates"],
    }),
    getCreatorRewardsAnalytics: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorRewardsAnalyticsData`),
      }),
      providesTags: ["RewardsAnalytics"],
    }),
    getPostOrCollectionLifetimeFunnel: builder.query({
      query: ({ type, postOrCollectionId }) => ({
        url: getQueryStringWithCreator(
          `c/getPostOrCollectionLifetimeFunnel?type=${type}&postOrCollectionId=${postOrCollectionId}`
        ),
      }),
      providesTags: ["PostOrCollectionLifetimeFunnel"],
    }),
    getPostOrCollectionEarnings: builder.query({
      query: ({ type, postOrCollectionId, startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getPostOrCollectionEarnings?type=${type}&postOrCollectionId=${postOrCollectionId}&startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["PostOrCollectionLifetimeEarnings"],
    }),
    getPostOrCollectionProductEarnings: builder.query({
      query: ({ type, postOrCollectionId }) => ({
        url: getQueryStringWithCreator(
          `c/getPostOrCollectionProductEarnings?type=${type}&postOrCollectionId=${postOrCollectionId}`
        ),
      }),
      providesTags: ["PostOrCollectionProductEarnings"],
    }),
    getSortAndFiltersForAnalytics: builder.query({
      query: ({ startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getSortAndFiltersForAnalytics?startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["AnalyticsFilterAndSortData"],
    }),
    getInstaBasicProfileData: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getInstaBasicProfileData`),
      }),
      providesTags: ["InstaBasicData"],
    }),
    getProductAnalytics: builder.query({
      query: ({ productId, startDate, endDate }) => ({
        url: getQueryStringWithCreator(
          `c/getProductAnalytics?&productId=${productId}&startDate=${startDate}&endDate=${endDate}`
        ),
      }),
      providesTags: ["ProductAnalytics"],
    }),
    getCampaignDetails: builder.query({
      query: ({ campaignId }) =>
        getQueryStringWithCreator(
          `getCampaignDetails?campaignID=${campaignId}`
        ),
    }),
    getRewardsLeaderboard: builder.query({
      query: ({ campaignId }) =>
        getQueryStringWithCreator(
          `getRewardsLeaderboardData?campaignID=${campaignId}`
        ),
    }),
    getOverlays: builder.query({
      query: ({ overlays }) => ({
        url: getQueryStringWithCreator(`c/getOverlays?overlays=${overlays}`),
      }),
      providesTags: ["Overlays"],
    }),
    getMeeshoQr: builder.query({
      query: ({ post_type, meesho_urls }) => {
        const params = new URLSearchParams();
        params.append("post_type", post_type);
        meesho_urls.forEach((url) => {
          params.append("meesho_urls", url);
        });
        const queryString = params.toString();
        const urlWithParams = `c/getMeeshoQRCodeForProducts?${queryString}`;
        return getQueryStringWithCreator(urlWithParams);
      },
    }),
    getBrandRewards: builder.query({
      query: ({ campaignName }) =>
        getQueryStringWithCreator(
          `c/getBrandRewards?campaign_name=${campaignName}`
        ),
    }),
    getBrandRewardProducts: builder.query({
      query: ({ brandId, page = 1, limit = 20 }) =>
        getQueryStringWithCreator(
          `c/getBrandRewardProducts?brand_id=${brandId}&page=${page}&limit=${limit}`
        ),
      transformResponse: (res) => res,
    }),
    getBrandRewardMaxMetrics: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `c/getBrandRewardMaxMetrics?brand_id=${brandId}`
        ),
      transformResponse: (res) => res?.data || {},
    }),
    getOffdutyRewardConstant: builder.query({
      query: () => getQueryStringWithCreator(`c/getOffdutyRewardConstant`),
      transformResponse: (res) => res?.data || {},
    }),
    getWishlinkConstant: builder.query({
      query: ({ key }) =>
        getQueryStringWithCreator(
          `getWishlinkConstant?key=${encodeURIComponent(key)}`
        ),
      transformResponse: (res) => res?.data?.value,
    }),
    getBrandCollectionProducts: builder.query({
      query: ({ collectionIds, page = 1, limit = 20 }) =>
        getQueryStringWithCreator(
          `c/getBrandCollectionProducts?collection_ids=${collectionIds}&page=${page}&limit=${limit}`
        ),
      transformResponse: (res) => res,
    }),
    getBrandRewardProductWishlink: builder.mutation({
      query: ({ id, brandId, discoverCopyOrigin }) => ({
        url: "c/getBrandRewardProductWishlink",
        method: "POST",
        body: postQueryBodyWithCreator({
          id,
          brand_id: brandId,
          ...(discoverCopyOrigin
            ? { discover_copy_origin: discoverCopyOrigin }
            : {}),
        }),
      }),
    }),
    getBrandExperimentsForCreator: builder.query({
      query: () => getQueryStringWithCreator(`c/getBrandExperimentsForCreator`),
      transformResponse: (res) => res?.data || [],
    }),
    getBrandExperimentDetailForCreator: builder.query({
      query: ({ experimentId }) =>
        getQueryStringWithCreator(
          `c/getBrandExperimentDetailForCreator?experiment_id=${experimentId}`
        ),
      transformResponse: (res) => res?.data || {},
    }),
    getBrandDetailForCreator: builder.query({
      query: ({ brandId }) =>
        getQueryStringWithCreator(
          `c/getBrandDetailForCreator?brand_id=${brandId}`
        ),
      transformResponse: (res) => res?.data || null,
    }),
    getBrandExperimentProductsForCreator: builder.query({
      query: ({ experimentId, page = 1, pageSize = 20 }) =>
        getQueryStringWithCreator(
          `c/getBrandExperimentProductsForCreator?experiment_id=${experimentId}&page=${page}&page_size=${pageSize}`
        ),
      transformResponse: (res) => res?.data || {},
    }),
    getNykaaAprilRewards: builder.query({
      query: () => getQueryStringWithCreator(`c/getNykaaAprilRewards`),
    }),

    getRewardsSummary: builder.query({
      query: () => getQueryStringWithCreator(`c/getRewardsSummary`),
    }),
    getQuickAccessIndicators: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getQuickAccessIndicators`),
      }),
      transformResponse: (res) => res?.data || {},
      providesTags: ["QuickAccessIndicators"],
    }),
    getRewardsListingMeta: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getRewardsListingMeta`),
      }),
      transformResponse: (res) => res?.data || {},
      providesTags: ["RewardsListingMeta"],
    }),
    getEngageListingMeta: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getEngageListingMeta`),
      }),
      transformResponse: (res) => res?.data || {},
      providesTags: ["EngageListingMeta"],
    }),
    getStreaksListingMeta: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getStreaksListingMeta`),
      }),
      transformResponse: (res) => res?.data || {},
      providesTags: ["StreaksListingMeta"],
      keepUnusedDataFor: 0,
    }),
    markStreaksTabSeen: builder.mutation({
      query: ({ tab }) => ({
        url: `/c/markStreaksTabSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({ tab }),
      }),
      invalidatesTags: ["QuickAccessIndicators", "StreaksListingMeta"],
      async onQueryStarted({ tab }, { dispatch, queryFulfilled }) {
        const TAB_TO_PILL = {
          upcoming: "upcoming",
          live: "live",
          past: "past",
        };
        const pillKey = TAB_TO_PILL[tab];
        if (!pillKey) return;
        const patchResult = dispatch(
          reduxAPI.util.updateQueryData(
            "getStreaksListingMeta",
            undefined,
            (draft) => {
              if (draft[pillKey]) {
                draft[pillKey] = { type: "none", count: 0 };
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    markStreakMainTabSeen: builder.mutation({
      query: () => ({
        url: `/c/markStreakMainTabSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      invalidatesTags: ["StreaksListingMeta"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          reduxAPI.util.updateQueryData(
            "getStreaksListingMeta",
            undefined,
            (draft) => {
              draft.streak_tab_seen = true;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getMeeshoCatalog: builder.query({
      query: () => getQueryStringWithCreator(`c/getMeeshoCatalogDetails`),
      transformResponse: (res) => res?.data,
    }),
    getDateWiseOrderSummary: builder.query({
      query: ({ startDate, endDate, onlyShowReturnedOrders, page }) =>
        getQueryStringWithCreator(
          `c/getDateWiseOrderSummary?startDate=${startDate}&endDate=${endDate}&onlyShowReturnedOrders=${onlyShowReturnedOrders}&page=${page}`
        ),
    }),
    getOauthClientName: builder.query({
      query: ({ clientId }) =>
        getQueryStringWithCreator(
          `c/fetchOauthAppDetails?clientId=${clientId}`
        ),
    }),
    getOrderHistoryForDate: builder.query({
      query: ({ fetchDate, onlyShowReturnedOrders, page }) =>
        getQueryStringWithCreator(
          `c/getOrderHistoryForDate?fetchDate=${fetchDate}&onlyShowReturnedOrders=${onlyShowReturnedOrders}&page=${page}`
        ),
    }),
    fetchCreatorAddress: builder.query({
      query: () => getQueryStringWithCreator(`c/fetchCreatorAddress`),
      providesTags: ["Address"],
    }),
    fetchAddressById: builder.query({
      query: (id) =>
        getQueryStringWithCreator(`c/fetchAddressById?address_id=${id}`),
    }),
    deleteWishlinkAccountRequest: builder.mutation({
      query: () => ({
        url: "c/deleteAccountRequest",
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
    }),
    deactivateWishlinkAccount: builder.query({
      query: () => getQueryStringWithCreator(`c/deactivateAccount`),
    }),
    getPostOrCollectionLifetimeLinkClicks: builder.query({
      query: ({ type, postOrCollectionId }) =>
        getQueryStringWithCreator(
          `c/getPostOrCollectionLifetimeLinkClicks?type=${type}&postOrCollectionId=${postOrCollectionId}`
        ),
    }),
    getNotifications: builder.query({
      query: ({ notificationType, notificationPage }) =>
        getQueryStringWithCreator(
          `c/getNotifications?notificationType=${notificationType}&notificationPage=${notificationPage}`
        ),
    }),
    getEarlierNotifications: builder.query({
      query: ({ notificationType, notificationPage }) =>
        getQueryStringWithCreator(
          `c/getNotifications?notificationType=${notificationType}&notificationPage=${notificationPage}`
        ),
    }),
    getBellIconStatus: builder.query({
      query: () => getQueryStringWithCreator(`c/getBellIconStatus`),
    }),
    getCreatorReferralEarnings: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorReferralEarnings`),
    }),
    getRefererDetails: builder.query({
      query: ({ referee }) =>
        getQueryStringWithCreator(`c/getRefererDetails?referee=${referee}`),
    }),
    getCreatorReferralLedger: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorReferralLedger`),
    }),
    getCreatorReferralData: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorReferralData`),
    }),
    getScrapingFailureDomains: builder.query({
      query: () => getQueryStringWithCreator(`c/getScrapingFailureDomains`),
    }),
    getMarqueeDetails: builder.query({
      query: () => getQueryStringWithCreator(`c/getMarqueeDetails`),
    }),
    getExploreBannerDetails: builder.query({
      query: () =>
        getQueryStringWithCreator(
          APIS_ENDPOINT.getExploreBannerDetails.getEndpoint()
        ),
      transformResponse: (res) => res?.data,
    }),
    fetchCreatorType: builder.query({
      query: () => getQueryStringWithCreator(`c/fetchCreatorType`),
    }),

    //POST Queries
    postNewPostData: builder.mutation({
      query: (body) => ({
        url: "c/createEditShopPost",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["NewPost", "PostOrCollectionDetails"],
    }),
    postNewCollectionData: builder.mutation({
      query: (body) => ({
        url: "c/createEditShopCollection",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["NewCollection", "PostOrCollectionDetails"],
    }),
    postShopProductsData: builder.mutation({
      query: ({
        postId,
        collectionId,
        productLinks,
        type,
        customizationType,
        customEngageValues,
        added_via_catalog,
        added_via_existing_product,
      }) => ({
        url: "c/addShopProducts",
        method: "POST",
        body: postQueryBodyWithCreator({
          postId,
          productLinks,
          type,
          collectionId,
          customizationType,
          customEngageValues,
          added_via_catalog,
          added_via_existing_product,
        }),
      }),
      invalidatesTags: ["ProductLinks", "PostOrCollectionDetails"],
    }),
    // DIY Auto Scraping APIs
    getDiyConfig: builder.query({
      query: () => getQueryStringWithCreator("c/getDiyConfig"),
    }),
    autoScrapeProduct: builder.mutation({
      query: ({
        url,
        added_via_catalog,
        added_via_existing_product,
        client_og_data,
      }) => ({
        url: "c/autoScrapeProduct",
        method: "POST",
        body: postQueryBodyWithCreator({
          url,
          added_via_catalog,
          added_via_existing_product,
          client_og_data: trimClientOgData(client_og_data),
        }),
      }),
    }),
    finalizeProducts: builder.mutation({
      query: ({ postId, collectionId, postType = "post", task_url_pairs }) => ({
        url: "c/finalizeProducts",
        method: "POST",
        body: postQueryBodyWithCreator({
          postId,
          collectionId,
          postType,
          task_url_pairs,
        }),
      }),
      invalidatesTags: ["ProductLinks"],
    }),
    updatePostOrCollectionStatus: builder.mutation({
      query: ({
        is_alive,
        is_hidden,
        is_retracted,
        postId,
        collectionId,
        type,
        action_type = "",
        cross_post_platforms = null,
        follow_gate_enabled,
        ig_product_tag_enabled = false,
      }) => ({
        url: "c/updatePostOrCollectionStatus",
        method: "POST",
        body: postQueryBodyWithCreator({
          is_alive,
          is_hidden,
          postId,
          collectionId,
          is_retracted,
          type,
          action_type,
          cross_post_platforms,
          follow_gate_enabled,
          ig_product_tag_enabled,
        }),
      }),
      invalidatesTags: [
        "ProductLinks",
        "ShopCounts",
        "Posts",
        "Collections",
        "Products",
        "Drafts",
        "PostRedirects",
        "CollectionRedirects",
        "ProductRedirects",
        "PostOrCollectionDetails",
      ],
    }),
    updateProductStatus: builder.mutation({
      query: ({ products, customEngageProducts }) => ({
        url: "c/hideShowShopProducts",
        method: "POST",
        body: postQueryBodyWithCreator({
          products,
          customEngageProducts,
        }),
      }),
      invalidatesTags: ["ProductStatus"],
    }),
    editProduct: builder.mutation({
      query: (body) => ({
        url: "c/editProduct",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: [
        "Products",
        "ProductStatus",
        "ProductLinks",
        "postOrCollectionProducts",
        "PostOrCollectionDetails",
        "AnalyticsProducts",
        "ProductAnalytics",
        "PostOrCollectionProductEarnings",
      ],
    }),
    enableCustomEngage: builder.mutation({
      query: () => ({
        url: "c/toggleCustomEngage",
        method: "POST",
        body: postQueryBodyWithCreator({ enabled: true }),
      }),
      invalidatesTags: ["Milestones"],
    }),
    postYoutubeLogin: builder.mutation({
      query: (body) => ({
        url: "c/handleYoutubeLogin",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["YoutubeLogin", "QuickAccessIndicators"],
    }),
    postSendOTP: builder.mutation({
      query: ({ countryCode, phoneNumberWithoutCountryCode }) => ({
        url: "c/handleSendOTP",
        method: "POST",
        body: {
          countryCode,
          phoneNumberWithoutCountryCode,
        },
      }),
    }),
    postVerifyOTP: builder.mutation({
      query: ({
        countryCode,
        phoneNumberWithoutCountryCode,
        sendOTPResponse,
        OTP,
      }) => ({
        url: "c/handleVerifyOTP",
        method: "POST",
        body: {
          countryCode,
          phoneNumberWithoutCountryCode,
          OTP,
          sendOTPResponse,
        },
      }),
    }),
    postAuthenticateOTPLess: builder.mutation({
      query: ({ token }) => ({
        url: "c/authenticateOTPLess",
        method: "POST",
        body: {
          token,
        },
      }),
    }),
    postLogOTPAuthStatus: builder.mutation({
      query: ({
        countryCode,
        phoneNumberWithoutCountryCode,
        service,
        state,
        verificationId,
        isSuccess,
        message,
      }) => ({
        url: "c/logOTPAuthStatus",
        method: "POST",
        body: {
          countryCode,
          phoneNumberWithoutCountryCode,
          service,
          state,
          verificationId,
          isSuccess,
          message,
        },
      }),
    }),
    postFirebaseLogin: builder.mutation({
      query: ({
        phoneNumberWithoutCountryCode,
        countryCode,
        uuid,
        allowWhatsappChat,
        source,
        is_boi_fk,
      }) => ({
        url: "c/handleFirebaseLogin",
        method: "POST",
        body: postQueryBodyWithCreator({
          phoneNumberWithoutCountryCode,
          countryCode,
          uuid,
          allowWhatsappChat,
          source,
          is_boi_fk,
        }),
      }),
      invalidatesTags: ["FirebaseLogin"],
    }),
    postSocialManualLogin: builder.mutation({
      query: (body) => ({
        url: "c/handleSocialLoginManually",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ManualSocialLogin", "QuickAccessIndicators"],
    }),
    postInstagramLogin: builder.mutation({
      query: (body) => ({
        url: "c/handleInstagramLogin",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["InstagramLogin", "QuickAccessIndicators"],
    }),
    postFacebookLogin: builder.mutation({
      query: (body) => ({
        url: "c/handleFacebookLogin",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    connectFacebookPage: builder.mutation({
      query: (body) => ({
        url: "c/handleFacebookPageConnection",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    getPagesForFacebookLogin: builder.query({
      query: (body) => ({
        url: getQueryStringWithCreator(`c/getPagesForFacebookLogin`),
        method: "GET",
      }),
      transformResponse: (res) => res?.data?.available_pages || [],
    }),
    postPinterestLogin: builder.mutation({
      query: (body) => ({
        url: "c/handlePinterestLogin",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PinterestLogin"],
    }),
    postSnapchatLogin: builder.mutation({
      query: (body) => ({
        url: "c/handleSnapchatLogin",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["SnapchatLogin"],
    }),
    getCrossPostingConfig: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCrossPostingConfig`),
      }),
    }),
    getCrossPostAndCommissions: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/crossPostAndCommissionsForCreator`),
      }),
      transformResponse: (res) => res?.data,
    }),
    crossPostStatusOnShop: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/crossPostStatusOnShop`),
      }),
      transformResponse: (res) => res?.data,
    }),
    dismissFailedCrossPostBanner: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`c/dismissFailedCrossPostBanner`),
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    allowedCrossPostingPlatforms: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/allowedCrossPostingPlatforms`),
      }),
    }),
    crossPostedPlatformsForPost: builder.query({
      query: ({ post_id }) => ({
        url: getQueryStringWithCreator(
          `c/crossPostedPlatformsForPost?post_id=${post_id}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    triggerCrossPost: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`c/triggerCrossPost`),
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getTokenViaAuthCode: builder.query({
      query: ({ auth_code }) => ({
        url: getQueryStringWithCreator(`auth/oauth-auth-code-login`),
        method: "POST",
        body: postQueryBodyWithCreator({
          auth_code,
        }),
      }),
    }),
    postCrossPostingConfig: builder.mutation({
      query: ({
        pinterest_cross_posting_enabled,
        youtube_cross_posting_enabled,
        facebook_cross_posting_enabled,
        snapchat_cross_posting_enabled,
      }) => ({
        url: "c/updateCrossPostingConfig",
        method: "POST",
        body: postQueryBodyWithCreator({
          pinterest_cross_posting_enabled,
          youtube_cross_posting_enabled,
          facebook_cross_posting_enabled,
          snapchat_cross_posting_enabled,
        }),
      }),
      transformResponse: (res) => res?.data,
    }),
    postConfirmCreatorUsername: builder.mutation({
      query: ({ updateUsername, newUsername }) => ({
        url: "c/confirmCreatorUsername",
        method: "POST",
        body: postQueryBodyWithCreator({
          updateUsername,
          newUsername,
        }),
      }),
      invalidatesTags: ["Creator"],
    }),
    postFilteredBrandsData: builder.mutation({
      query: ({
        limit,
        offerOnly,
        page,
        tags,
        search,
        onSale,
        sourcingOnly,
        sortByCommissions,
        fetchOnlyFavoriteBrands,
      }) => ({
        url: `c/fetchFilteredBrands`,
        method: "POST",
        body: postQueryBodyWithCreator({
          limit,
          offerOnly,
          page,
          tags,
          search,
          onSale,
          sourcingOnly,
          sortByCommissions,
          fetchOnlyFavoriteBrands,
        }),
      }),
      invalidatesTags: ["FilteredBrands"],
    }),
    postSingleProductLink: builder.mutation({
      query: ({ link, client_og_data }) => ({
        url: `c/convertSingleProductLink`,
        method: "POST",
        body: postQueryBodyWithCreator({
          link,
          ...(client_og_data && {
            client_og_data: trimClientOgData(client_og_data),
          }),
        }),
      }),
    }),
    updateCreatorProfile: builder.mutation({
      query: (body) => ({
        url: `c/updateCreatorProfile`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Creator"],
    }),
    submitCreatorBankDetails: builder.mutation({
      query: ({ accountNumber, pan, bankName, ifsc }) => ({
        url: `c/submitCreatorBankDetails`,
        method: "POST",
        body: postQueryBodyWithCreator({
          accountNumber,
          pan,
          bankName,
          ifsc,
        }),
      }),
      invalidatesTags: ["BankDetails"],
    }),
    updateCreatorTemplates: builder.mutation({
      query: (body) => ({
        url: `c/updateCreatorTemplates`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Templates"],
    }),
    login: builder.mutation({
      query: (body) => ({
        url: `/login`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    disconnectSnapchat: builder.mutation({
      query: (body) => ({
        url: `/c/disconnectSnapchat`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SnapchatLogin"],
    }),
    disconnectFacebook: builder.mutation({
      query: (body) => ({
        url: `/disconnectFacebook`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    connectFacebook: builder.mutation({
      query: (body) => ({
        url: `/c/connectEngage`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["EngageListingMeta"],
    }),
    getCreatorFacebookEngageStatus: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorFacebookEngageStatus`),
      }),
      transformResponse: (res) => res?.data,
    }),
    updateCreatorFacebookEngageSettings: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorFacebookEngageSettings`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["EngageListingMeta"],
    }),
    getCreatorFacebookKeywords: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorFacebookKeywords`),
      }),
      transformResponse: (res) => res?.data?.keywords_with_status || [],
    }),
    addCreatorFacebookKeyword: builder.mutation({
      query: (body) => ({
        url: `/c/addCreatorFacebookKeyword`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateCreatorFacebookKeywords: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorFacebookKeywords`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getCreatorFacebookTemplates: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorFacebookTemplates`),
      }),
      transformResponse: (res) => res?.data?.templates || [],
    }),
    addCreatorFacebookTemplate: builder.mutation({
      query: (body) => ({
        url: `/c/addCreatorFacebookTemplate`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateCreatorFacebookTemplate: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorFacebookTemplate`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    removeCreatorFacebookTemplate: builder.mutation({
      query: (body) => ({
        url: `/c/removeCreatorFacebookTemplate`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateCreatorDashboardMilestonesStatus: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorDashboardMilestonesStatus`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Milestones"],
    }),
    updateCTKsettings: builder.mutation({
      query: (body) => ({
        url: `/c/updateCTKsettings`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["InstaBasicData", "EngageListingMeta"],
    }),
    collectCreatorFeedback: builder.mutation({
      query: (body) => ({
        url: `/c/collectCreatorFeedback`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Feedback"],
    }),
    updateCreatorAddress: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorAddress`,
        method: "PATCH",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Address"],
    }),
    updateEngageGtmFlag: builder.mutation({
      query: (body) => ({
        url: `/c/updateEngageGtmFlag`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Creator"],
    }),
    updatePayoutsGtmFlag: builder.mutation({
      query: (body) => ({
        url: `/c/updatePayoutsGtmFlag`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Creator"],
    }),
    createNewAddress: builder.mutation({
      query: (body) => ({
        url: `/c/createNewAddress`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Address"],
    }),
    markNotificationAsRead: builder.mutation({
      query: (body) => ({
        url: `/c/markNotificationAsRead`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateRewardsFocus: builder.mutation({
      query: (body) => ({
        url: `/c/updateRewardsFocus`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateCreatorDemoDetails: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorDemoData`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["CreatorDemo"],
    }),
    updateCreatorBPCMeetDetails: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorBpcContentMeetData`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["CreatorBPCMeet"],
    }),
    updateFirstLinkSkipped: builder.mutation({
      query: (body) => ({
        url: `/c/markFirstLinkSkipped`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Milestones", "authStatus"],
    }),
    updateOverlayStatusAsRead: builder.mutation({
      query: (body) => ({
        url: `/c/markOverlayAsRead`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
        invalidatesTags: ["Overlays"],
      }),
    }),
    resetOverlayStatus: builder.mutation({
      query: (body) => ({
        url: `/c/resetOverlayStatus`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
        invalidatesTags: ["Overlays"],
      }),
    }),
    updateAppUtm: builder.mutation({
      query: (body) => ({
        url: `/c/updateAppUtm`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getCreatorDashboardSource: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`/c/getCreatorDashboardSource`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getDiyRewardModule: builder.query({
      query: ({ postId }) => ({
        url: getQueryStringWithCreator(`c/getDiyRewardModule?postId=${postId}`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getSeleqtDiyCaptionNudge: builder.query({
      query: ({ postId }) => ({
        url: getQueryStringWithCreator(
          `c/getSeleqtDiyCaptionNudge?postId=${postId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getPostNudges: builder.query({
      query: ({ postId }) => ({
        url: getQueryStringWithCreator(`c/getPostNudges?postId=${postId}`),
      }),
      transformResponse: (res) => res?.data?.nudges || [],
    }),
    getCommissionFilters: builder.query({
      query: ({
        pendingOrConfirmed,
        payoutId = null,
        isPastPayout = false,
      }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getCommissionFilters", {
            pendingOrConfirmed,
            payout_id: payoutId,
            for_payouts: isPastPayout,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCommissionsDetails: builder.query({
      query: ({
        pendingOrConfirmed,
        payoutId = null,
        isPastPayout = false,
        month,
        year,
      }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getCommissionDetails", {
            pendingOrConfirmed,
            payout_id: payoutId,
            for_payouts: isPastPayout,
            month,
            year,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCommissionsDetailsByBrandId: builder.query({
      query: ({
        pendingOrConfirmed,
        brandId,
        payoutId,
        isPastPayout = false,
        month,
        year,
        wishlinksOrYoutube,
      }) => ({
        url: getQueryStringWithCreator(
          `/c/getCommissionDetails?pendingOrConfirmed=${pendingOrConfirmed}&brandId=${brandId}&payout_id=${payoutId}&for_payouts=${isPastPayout}&month=${month}&year=${year}&wishlinksOrYoutube=${wishlinksOrYoutube}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCurrentPayoutSummary: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCurrentPayoutSummary`),
      }),
      transformResponse: (res) => res?.data,
    }),
    // Net-new (Payouts CX only) — points at the V2 URL alias directly since
    // there is no pre-existing schema to preserve for this endpoint.
    getPayoutStatusCards: builder.query({
      query: ({ page = 1, pageSize = 10, scope } = {}) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("c/getPayoutStatusCardsV2", {
            page,
            pageSize,
            scope,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getPastPayoutList: builder.query({
      query: ({ page, pageSize, payout_type, period_start, period_end }) => {
        const params = new URLSearchParams({
          page: String(page ?? 1),
          pageSize: String(pageSize ?? 10),
        });

        if (payout_type) {
          params.set("payout_type", payout_type);
        }
        if (period_start) {
          params.set("period_start", period_start);
        }
        if (period_end) {
          params.set("period_end", period_end);
        }

        return {
          url: getQueryStringWithCreator(
            `c/getPastPayoutList?${params.toString()}`
          ),
        };
      },
      transformResponse: (res) => res?.data,
    }),
    // Payouts CX variant of getPastPayoutList — card-based, sorted by actual terminal date.
    // Returns the full history list; FE applies filters + scroll pagination.
    getPastPayoutListV2: builder.query({
      query: (args = {}) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("c/getPastPayoutListV2", {
            page: args?.page,
            pageSize: args?.pageSize,
            payout_type: args?.payout_type,
            period_start: args?.period_start,
            period_end: args?.period_end,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getSinglePastPayoutDetails: builder.query({
      query: ({ id = null, is_handmade_payout }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getSinglePastPayoutDetails", {
            payoutId: id,
            is_handmade_payout,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    // Payouts CX variant of getSinglePastPayoutDetails — enriched with card_state/card_component/etc.
    getSinglePastPayoutDetailsV2: builder.query({
      query: ({ id, is_handmade_payout }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getSinglePastPayoutDetailsV2", {
            payoutId: id,
            is_handmade_payout,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getPastPayoutBrandDetails: builder.query({
      query: ({
        page,
        pageSize,
        is_handmade_payout,
        payoutId = null,
        month,
        year,
        wishlinksOrYoutube,
      }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getPastPayoutBrandDetails", {
            page,
            pageSize,
            is_handmade_payout,
            payoutId,
            month,
            year,
            wishlinksOrYoutube,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCommissionsBrandDetails: builder.query({
      query: ({
        page,
        pageSize,
        pendingOrConfirmed,
        month,
        year,
        wishlinksOrYoutube,
      }) => ({
        url: getQueryStringWithCreator(
          buildPayoutQueryPath("/c/getCommissionsBrandDetails", {
            page,
            pageSize,
            pendingOrConfirmed,
            month,
            year,
            wishlinksOrYoutube,
          })
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCommissionsDateWiseOrderLedger: builder.query({
      query: ({
        page,
        pageSize,
        pendingOrConfirmed,
        brandId,
        payoutId,
        isPastPayout = false,
        month,
        year,
        wishlinksOrYoutube,
      }) => ({
        url: getQueryStringWithCreator(
          `/c/getCommissionsDateWiseOrderLedger?page=${page}&pageSize=${pageSize}&pendingOrConfirmed=${pendingOrConfirmed}&brandId=${brandId}&payout_id=${payoutId}&for_payouts=${isPastPayout}&month=${month}&year=${year}&wishlinksOrYoutube=${wishlinksOrYoutube}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCommissionsSingleDateOrderLedger: builder.query({
      query: ({
        page,
        pageSize,
        pendingOrConfirmed,
        brandId,
        date,
        payoutId,
        isPastPayout = false,
        month,
        year,
        wishlinksOrYoutube,
      }) => ({
        url: getQueryStringWithCreator(
          `/c/getCommissionsSingleDateOrderLedger?page=${page}&pageSize=${pageSize}&pendingOrConfirmed=${pendingOrConfirmed}&brandId=${brandId}&date=${date}&payout_id=${payoutId}&for_payouts=${isPastPayout}&month=${month}&year=${year}&wishlinksOrYoutube=${wishlinksOrYoutube}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    submitCreatorReturnsRequest: builder.mutation({
      query: (body) => ({
        url: `/c/submitCreatorReturnsRequest`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getPendingPayout: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`/c/getPendingPayout`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCategoryWiseBrandCommission: builder.query({
      query: ({ brandId }) => ({
        url: getQueryStringWithCreator(
          `/c/fetchCategoryWiseBrandCommissions?brand_id=${brandId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    updateGtmAsCompleted: builder.mutation({
      query: (body) => ({
        url: `/c/updateGtmAsCompleted`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Creator"],
    }),
    fetchCreatorConfirmedNudgeDetails: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`/c/fetchCreatorConfirmedNudgeDetails`),
      }),
      transformResponse: (res) => res?.data?.nudges,
    }),
    fetchCreatorBrandConfirmedNudgeDetails: builder.query({
      query: ({ brandId }) => ({
        url: getQueryStringWithCreator(
          `/c/fetchCreatorBrandConfirmedNudgeDetails?brandId=${brandId}`
        ),
      }),
      transformResponse: (res) => res?.data?.nudges,
    }),
    markPayoutSeen: builder.mutation({
      query: (body) => ({
        url: `/c/markPayoutSeen`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    submitCreatorFreshdeskRestoreId: builder.mutation({
      query: ({ restoreId }) => ({
        url: `/c/submitCreatorFreshdeskRestoreId`,
        method: "POST",
        body: postQueryBodyWithCreator({
          restore_id: restoreId,
        }),
      }),
    }),
    submitCreatorFreshdeskAttributes: builder.mutation({
      query: () => ({
        url: `/c/submitCreatorFreshdeskAttributes`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
    }),
    getAllRewards: builder.query({
      query: (params = {}) => ({
        url: getQueryStringWithCreator(
          `c/getAllRewards?${params?.brands ? `brands=${params?.brands}` : ""}`
        ),
      }),
      transformResponse: (res) => res?.data,
      providesTags: ["AllRewards"],
    }),
    // Paginated variant used by the main WishlinkRewards listing (CAT-311).
    // Mirrors the streaks endpoint's serializeQueryArgs + merge + forceRefetch
    // pattern (`src/services/streaksService.js`), swapping cursor for offset.
    // Shares the `AllRewards` cache tag so any invalidation (e.g. optInReward)
    // refreshes both the legacy and paginated buckets in one shot.
    getAllRewardsPaginated: builder.query({
      query: (params = {}) => {
        const search = new URLSearchParams();
        if (params?.page != null) search.set("page", String(params.page));
        if (params?.page_size != null)
          search.set("page_size", String(params.page_size));
        if (params?.tab) search.set("tab", params.tab);
        if (params?.opted_in != null)
          search.set("opted_in", String(params.opted_in));
        if (params?.parent_campaign_name)
          search.set("parent_campaign_name", params.parent_campaign_name);
        if (params?.brands) search.set("brands", params.brands);
        const qs = search.toString();
        return {
          url: getQueryStringWithCreator(
            `c/getAllRewards${qs ? `?${qs}` : ""}`
          ),
        };
      },
      transformResponse: (res) => res?.data,
      providesTags: ["AllRewards"],
      // Every filter arg belongs in the cache key so filter changes get their
      // own bucket; `page` is deliberately omitted so successive pages merge
      // into the same bucket (see `merge` below).
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { tab, opted_in, parent_campaign_name, brands, page_size } =
          queryArgs || {};
        return `${endpointName}:${JSON.stringify({
          tab: tab ?? null,
          opted_in: opted_in ?? null,
          parent_campaign_name: parent_campaign_name ?? null,
          brands: brands ?? null,
          page_size: page_size ?? null,
        })}`;
      },
      merge: (currentCache, incoming, { arg }) => {
        // Page 1 (or unset) always replaces the whole bucket.
        const page = arg?.page ?? 1;
        if (page <= 1 || !currentCache?.rewards) {
          return incoming;
        }
        // Replace existing entries by id, then append truly-new ones.
        // This handles both the happy path (page 2 ids never overlap page 1)
        // AND a refetch of the same page triggered by ``AllRewards`` cache
        // invalidation on ``optInReward``. RTK stores only the last
        // ``originalArgs`` per bucket (page is excluded from the cache key),
        // so an invalidation-triggered refetch replays the last-seen page N
        // — a blind append would duplicate rewards N*10..N*10+9. Dedupe by
        // id also absorbs any race between the load-more sentinel firing
        // and an invalidation refetch happening concurrently.
        const incomingIds = new Set(
          (incoming?.rewards ?? []).map((r) => r?.id).filter((id) => id != null)
        );
        const kept = currentCache.rewards.filter(
          (r) => r?.id == null || !incomingIds.has(r.id)
        );
        return {
          rewards: [...kept, ...(incoming?.rewards ?? [])],
          meta: incoming?.meta ?? currentCache.meta,
        };
      },
      // Any change in filter or page → new fetch. Same shape as streaks.
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg) return true;
        const keys = [
          "page",
          "page_size",
          "tab",
          "opted_in",
          "parent_campaign_name",
          "brands",
        ];
        return keys.some((k) => currentArg?.[k] !== previousArg?.[k]);
      },
      // Remount refetches page 1; accumulated pages lost by design so the
      // user never lands on a stale mid-list state. Matches streaks.
      keepUnusedDataFor: 0,
    }),
    getAllRewardsLite: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getAllRewardsLite`),
      }),
      transformResponse: (res) => res?.data,
      providesTags: ["AllRewards"],
    }),
    getSpecificRewardDetails: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getSpecificRewardDetails?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getWishlinkRewardDetails: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getWishlinkRewardDetails?reward_id=${rewardId}`
        ),
      }),
      // Lets updateRewardAccordionState invalidate this so accordion_state stays in sync
      // when switching tabs (no hard refresh).
      providesTags: (result, error, { rewardId }) => [
        { type: "WishlinkRewardDetails", id: rewardId },
      ],
    }),
    getRewardEligibleProducts: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getRewardEligibleProducts?reward_id=${rewardId}`
        ),
      }),
    }),
    getRewardSourcedProducts: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getRewardSourcedProducts?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getRewardRelatedPosts: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getRewardRelatedPosts?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getLeaderboardRewardAmount: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getLeaderboardRewardAmount?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getLeaderboardRanks: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getLeaderboardRanks?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    updateRewardAccordionState: builder.mutation({
      query: ({
        rewardId,
        whatQualifiesExpanded,
        whatDoesntQualifyExpanded,
      }) => ({
        url: `/c/updateRewardAccordionState`,
        method: "POST",
        body: postQueryBodyWithCreator({
          reward_id: rewardId,
          ...(whatQualifiesExpanded !== undefined && {
            what_qualifies_expanded: whatQualifiesExpanded,
          }),
          ...(whatDoesntQualifyExpanded !== undefined && {
            what_doesnt_qualify_expanded: whatDoesntQualifyExpanded,
          }),
        }),
      }),
      invalidatesTags: (result, error, { rewardId }) => [
        { type: "WishlinkRewardDetails", id: rewardId },
      ],
    }),
    optInReward: builder.mutation({
      query: ({ rewardId }) => ({
        url: `/c/optInReward`,
        method: "PUT",
        body: postQueryBodyWithCreator({
          reward_id: rewardId,
        }),
      }),
      // Invalidate every consumer of the rewards list (legacy, paginated,
      // and lite variants share the `AllRewards` tag) so the newly-opted-in
      // status reflects everywhere without a manual refetch (CAT-311).
      invalidatesTags: ["AllRewards"],
    }),
    markRewardsListingSeen: builder.mutation({
      query: () => ({
        url: `/c/markRewardsListingSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markSourcingListingSeen: builder.mutation({
      query: ({ isBrandSourcing = false } = {}) => ({
        url: `/c/markSourcingListingSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          is_brand_sourcing: isBrandSourcing,
        }),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markSourcingOrdersSeen: builder.mutation({
      query: ({ isBrandSourcing = false } = {}) => ({
        url: `/c/markSourcingOrdersSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          is_brand_sourcing: isBrandSourcing,
        }),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markSourcingEntitySeen: builder.mutation({
      query: ({ entityType, entityId }) => ({
        url: `/c/markSourcingEntitySeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          entity_type: entityType,
          entity_id: entityId,
        }),
      }),
      invalidatesTags: ["brandSourcingCollections"],
    }),
    markRewardDetailSeen: builder.mutation({
      query: ({ rewardId }) => ({
        url: `/c/markRewardDetailSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          reward_id: rewardId,
        }),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markRewardSeen: builder.mutation({
      query: ({ rewardId }) => ({
        url: `/c/markRewardSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          reward_id: rewardId,
        }),
      }),
      invalidatesTags: ["QuickAccessIndicators", "RewardsListingMeta"],
    }),
    markSelectByWishlinkListingSeen: builder.mutation({
      query: () => ({
        url: `/c/markSelectByWishlinkListingSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markGrowthListingSeen: builder.mutation({
      query: () => ({
        url: `/c/markGrowthListingSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    markQuickAccessIndicatorSeen: builder.mutation({
      query: ({ signals }) => ({
        url: `c/markQuickAccessIndicatorSeen`,
        method: "POST",
        body: postQueryBodyWithCreator({ signals }),
      }),
      invalidatesTags: ["QuickAccessIndicators"],
    }),
    getCreatorRewardMilestoneDataForBrand: builder.query({
      query: ({ brandId }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorRewardMilestoneDataForBrand?brand_id=${brandId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCreatorRewardMilestoneProductsData: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorRewardMilestoneProductsData?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getTotalViewsForCreator: builder.query({
      query: ({ rewardId }) => ({
        url: getQueryStringWithCreator(
          `c/getTotalViewsForCreator?reward_id=${rewardId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    webengageEventTrigger: builder.mutation({
      query: ({ event_source, event_value = null, event_obj = null }) => ({
        url: `/we/triggerEvent`,
        method: "POST",
        body: postQueryBodyWithCreator({
          event_source: event_source,
          event_value: event_value,
          event_obj: event_obj,
        }),
      }),
    }),
    getMonthYearListSincePayoutV2: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getMonthYearListSincePayoutV2`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getPendingConfirmedCommissionSplit: builder.query({
      query: ({ month, year }) => ({
        url: getQueryStringWithCreator(
          `c/getPendingConfirmedCommissionSplit?month=${month}&year=${year}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getConfirmedCommissionSplitInPayouts: builder.query({
      query: ({ month, year }) => ({
        url: getQueryStringWithCreator(
          `c/getConfirmedCommissionSplitInPayouts?month=${month}&year=${year}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getSimilarProductRecommendations: builder.query({
      query: ({ postType = "post", postOrCollectionId = 0 }) => ({
        url: getQueryStringWithCreator(
          `c/getSimilarProductRecommendations?postOrCollectionId=${postOrCollectionId}&postType=${postType}`
        ),
      }),
    }),
    engageDebuggerReport: builder.mutation({
      query: ({ postUrl = null }) => ({
        url: getQueryStringWithCreator(`c/getCreatorEngageDebuggerResult`),
        method: "POST",
        body: postQueryBodyWithCreator({ post_url: postUrl }),
      }),
      transformResponse: (res) => res?.data,
    }),
    getEngageDebuggerReport: builder.query({
      query: ({ postUrl = null }) => ({
        url: getQueryStringWithCreator(`c/getCreatorEngageDebuggerResult`),
        method: "POST",
        body: postQueryBodyWithCreator({ post_url: postUrl }),
      }),
      transformResponse: (res) => res?.data?.debug_report,
    }),
    getAllBrandDeals: builder.query({
      query: ({ is_home = false, brands }) => {
        const baseUrl = `c/getAllBrandDeals`;
        let url = `${baseUrl}?is_home=${is_home}`;
        if (brands) {
          url += `&brands=${brands}`;
        }
        return { url: getQueryStringWithCreator(url) };
      },
      transformResponse: (res) => res?.data,
    }),
    getAllBrandDealCollection: builder.query({
      query: ({
        product_limit,
        deal_limit,
        is_randomized = false,
        is_home = false,
        brands,
      }) => {
        const baseUrl = `c/getAllBrandDealCollection?product_limit=${product_limit}&is_randomized=${is_randomized}&is_home=${is_home}&deal_limit=${deal_limit}`;
        const url = brands ? `${baseUrl}&brands=${brands}` : baseUrl;
        return { url: getQueryStringWithCreator(url) };
      },
      transformResponse: (res) => res?.data,
    }),
    getBpcCollection: builder.query({
      query: ({ product_limit, brands }) => {
        const baseUrl = `c/getBpcCollection?product_limit=${product_limit}`;
        const url = brands ? `${baseUrl}&brands=${brands}` : baseUrl;
        return { url: getQueryStringWithCreator(url) };
      },
      transformResponse: (res) => res?.data,
    }),
    getBrandDealDetail: builder.query({
      query: ({ collectionId }) => ({
        url: getQueryStringWithCreator(`c/getBrandDealDetail/${collectionId}`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getRecommendedPostsForGrowth: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getRecommendedPostsForGrowth`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getRecommendedProductsForPost: builder.query({
      query: ({ postId }) => ({
        url: getQueryStringWithCreator(
          `c/getRecommendedProductsForPost?post_id=${postId}`
        ),
      }),
      transformResponse: (res) => res?.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `endpointName=${endpointName}&creatorId=${queryArgs?.creatorId}`;
      },
    }),
    getBrandTopOffer: builder.query({
      query: ({ is_home = false, brands }) => {
        const baseUrl = `c/brandTopOffer`;
        let url = `${baseUrl}?is_home=${is_home}`;
        if (brands) {
          url += `&brands=${brands}`;
        }
        return { url: getQueryStringWithCreator(url) };
      },
      transformResponse: (res) => res?.data,
    }),
    getD2CCollection: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/D2CCollection`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getD2CCollectionRetrieve: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`c/D2CCollectionRetrieve/${id}`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getRewardExploreBanner: builder.query({
      query: ({ brands }) => {
        const url = brands
          ? getQueryStringWithCreator(
              `c/getAllRewards?is_explore_home=true&brands=${brands}`
            )
          : getQueryStringWithCreator(`c/getAllRewards?is_explore_home=true`);
        return { url };
      },
      transformResponse: (res) => res?.data,
    }),

    getContentInspirationsByIds: builder.query({
      query: ({ is_home = false, brands, category }) => ({
        url: getQueryStringWithCreator(
          `c/contentInspiration?brands=${brands}&category=${category}&is_home=${is_home}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getContentInspirationV2: builder.query({
      query: ({
        seed,
        limit = 20,
        offset = 0,
        brands,
        product_category,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("seed", seed);
        params.set("limit", String(limit));
        params.set("offset", String(offset));
        if (brands) params.set("brands", brands);
        if (product_category) params.set("product_category", product_category);
        return {
          url: getQueryStringWithCreator(
            `c/contentInspirationV2?${params.toString()}`
          ),
        };
      },
    }),
    getContentInspirationV2Rail: builder.query({
      query: () => ({
        url: getQueryStringWithCreator("c/contentInspirationV2Rail"),
      }),
    }),
    getContentInspirationV2Filters: builder.query({
      query: () => ({
        url: getQueryStringWithCreator("c/contentInspirationV2Filters"),
      }),
      transformResponse: (res) => res?.data,
    }),
    getDiscoverSearchAutosuggest: builder.query({
      query: ({ search }) => ({
        url: getQueryStringWithCreator(
          `c/discoverSearchAutosuggest?search=${encodeURIComponent(search)}`
        ),
      }),
      transformResponse: (res) => (res?.suggestions ?? []).slice(0, 5),
    }),
    getDiscoverSearchContent: builder.query({
      query: ({
        searchString,
        tab = "REELS",
        limit = 20,
        offset = 0,
        sortBy,
        filters,
      }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        params.set("tab", tab);
        params.set("limit", String(limit));
        params.set("offset", String(offset));
        if (sortBy) params.set("sortBy", sortBy);
        if (filters) params.set("filters", filters);
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchContent?${params.toString()}`
          ),
        };
      },
    }),
    getDiscoverSearchFilterBrands: builder.query({
      query: ({ searchString, filters }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        if (filters) params.set("filters", filters);
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchFilterBrands?${params.toString()}`
          ),
        };
      },
    }),
    getDiscoverSearchFilterCategories: builder.query({
      query: ({ searchString, filters }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        if (filters) params.set("filters", filters);
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchFilterCategories?${params.toString()}`
          ),
        };
      },
    }),
    getDiscoverSearchFilterGenders: builder.query({
      query: ({ searchString, filters }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        if (filters) params.set("filters", filters);
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchFilterGenders?${params.toString()}`
          ),
        };
      },
    }),
    getDiscoverSearchFilterCount: builder.query({
      query: ({ searchString, filters }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        if (filters) params.set("filters", filters);
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchFilterCount?${params.toString()}`
          ),
        };
      },
    }),
    getDiscoverSearchDynamicFilters: builder.query({
      query: ({ searchString }) => ({
        url: getQueryStringWithCreator(
          `c/discoverSearchDynamicFilters?searchString=${encodeURIComponent(
            searchString
          )}`
        ),
      }),
    }),
    getDiscoverSearchQuickFilters: builder.query({
      query: ({ searchString, attributeFilters }) => {
        const params = new URLSearchParams();
        params.set("searchString", searchString);
        if (attributeFilters) {
          params.set("attributeFilters", JSON.stringify(attributeFilters));
        }
        return {
          url: getQueryStringWithCreator(
            `c/discoverSearchQuickFilters?${params.toString()}`
          ),
        };
      },
    }),
    getProductsFromPost: builder.query({
      query: ({ postId, brandId }) => {
        const queryParams = new URLSearchParams();
        if (postId) queryParams.append("post_id", String(postId));
        if (brandId) queryParams.append("brand_id", String(brandId));
        return {
          url: getQueryStringWithCreator(
            `c/getProductsFromPost?${queryParams.toString()}`
          ),
        };
      },
      transformResponse: (res) => res?.data ?? [],
    }),
    getAllCreatorPostFrames: builder.query({
      query: ({ brands = [], categories = [], subcategories = [] }) => {
        const queryParams = [];
        if (brands.length > 0) {
          queryParams.push(`brands=${brands.join(",")}`);
        }
        if (categories.length > 0) {
          queryParams.push(`categories=${categories.join(",")}`);
        }
        if (subcategories.length > 0) {
          queryParams.push(`subcategories=${subcategories.join(",")}`);
        }
        const queryString = `c/getAllCreatorPostFrames?${queryParams.join(
          "&"
        )}`;
        return {
          url: getQueryStringWithCreator(queryString),
        };
      },
      transformResponse: (res) => res?.data,
    }),
    brandExploreBannersRetrieveById: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`c/brandExploreBannersRetrieve/${id}`),
      }),
      transformResponse: (res) => res?.data,
    }),
    setCreatorFestiveBannerFlag: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/setCreatorFestiveBannerFlag`),
      }),
      transformResponse: (res) => res?.data,
    }),
    getHomeTopEcommerce: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/brandExploreBanners`),
      }),
      transformResponse: (res) => res?.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `endpointName=${endpointName}&creatorId=${queryArgs?.creatorId}`;
      },
    }),
    getCreatorPostFrameDetails: builder.query({
      query: ({ frameId }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorPostFrameDetails?frameId=${frameId}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    fetchCreatorCollectionProductImage: builder.query({
      query: ({ product_id }) => ({
        url: getQueryStringWithCreator(
          `c/fetchCreatorCollectionProductImage?product_id=${product_id}`
        ),
      }),
      transformResponse: (res) => res?.image_urls,
    }),
    getCutouts: builder.mutation({
      query: (body) => ({
        url: `cutouts/`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getCutoutsById: builder.query({
      query: ({ id }) => ({
        url: `cutouts/?id=${id}`,
      }),
      transformResponse: (res) => res,
      providesTags: ["Collage"],
    }),
    deleteCutoutsById: builder.mutation({
      query: (body) => ({
        url: `cutouts/`,
        body,
        method: "DELETE",
      }),
      invalidatesTags: ["Collage"],
    }),
    uploadCutouts: builder.mutation({
      query: (body) => ({
        url: `cutouts/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Collage"],
    }),
    getCollageById: builder.query({
      query: ({ id, isPost }) => ({
        url: `cutouts/all/?${isPost ? `post_id=${id}` : `collection_id=${id}`}`,
      }),
      providesTags: ["Collage"],
    }),
    getSingleCollageDataById: builder.query({
      query: ({ id }) => ({
        url: `cutouts/getSingleCollageData?id=${id}`,
      }),
      providesTags: ["Collage"],
      transformResponse: (res) => res?.data,
    }),
    getEnhancedInstagramInsights: builder.query({
      query: ({ period = "30d" }) => ({
        url: getQueryStringWithCreator(
          `c/getEnhancedInstagramInsights?period=${period}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getVariantForFeature: builder.query({
      query: ({ feature_name }) => ({
        url: getQueryStringWithCreator(
          `c/featureFlags/getVariantForFeature/?feature_name=${feature_name}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCampaignStats: builder.query({
      query: ({ campaign_id }) => ({
        url: getQueryStringWithCreator(
          `c/campaignStats/?campaign_id=${campaign_id}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    optCreatorInFeature: builder.mutation({
      query: ({ feature_name, variant_name }) => ({
        url: `c/featureFlags/optCreatorInFeature/`,
        method: "POST",
        body: postQueryBodyWithCreator({
          feature_name,
          ...(variant_name && { variant_name }),
        }),
      }),
      transformResponse: (res) => res?.data,
    }),
    // CAT-543: count one forced show of the DIY education screens
    markDiyEducationForcedView: builder.mutation({
      query: () => ({
        url: `c/diyEducation/markForcedView/`,
        method: "POST",
        body: postQueryBodyWithCreator({}),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCreatorCampaign: builder.query({
      query: ({
        status_list,
        campaign_status_list,
        page = 1,
        page_size = 10,
      }) => {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(page_size),
        });
        if (status_list) {
          params.set("status_list", status_list);
        }

        if (campaign_status_list) {
          params.set("campaign_status_list", campaign_status_list);
        }

        return {
          url: getQueryStringWithCreator(
            `b/getCreatorCampaign?${params.toString()}`
          ),
        };
      },
      transformResponse: (res) => ({
        campaigns: res?.results || [],
        pagination: {
          count: res?.count || 0,
          total_pages: res?.total_pages || 1,
          next: res?.next,
          previous: res?.previous,
          current_page: res?.current_page || 1,
        },
      }),
    }),
    getCreatorTotalEarnings: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`b/getCreatorTotalEarnings`),
      }),
      providesTags: ["TotalEarnings"],
    }),
    getCreatorCollabHistory: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`b/getCreatorCollabHistory`),
      }),
      transformResponse: (response) => {
        if (response?.collab_history) {
          // Sort collab history by payout_date in descending order (newest first)
          // Handle null payout_date by putting them at the end
          const sortedResults = [...response.collab_history].sort((a, b) => {
            if (!a.payout_date && !b.payout_date) return 0;
            if (!a.payout_date) return 1; // Put null dates at the end
            if (!b.payout_date) return -1;
            return new Date(b.payout_date) - new Date(a.payout_date);
          });
          return {
            ...response,
            collab_history: sortedResults,
          };
        }
        return response;
      },
      providesTags: ["CollabHistory"],
    }),
    getCreatorCampaignByID: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`b/getVarientCreator/${id}`),
      }),
      transformResponse: (res) => {
        return { ...res?.data, message: res?.message };
      },
    }),
    updateCampaign: builder.mutation({
      query: ({ id, body }) => ({
        url: `b/getVarientCreator/${id}`,
        method: "PUT",
        body,
      }),
    }),
    triggerContentSubmissionTemplate: builder.mutation({
      query: ({ threadId }) => ({
        url: getQueryStringWithCreator(
          `c/chat/triggerContentSubmissionTemplate/${threadId}/`
        ),
        method: "POST",
      }),
    }),
    creatorDeliverableFileUpload: builder.query({
      query: (queryParams) => ({
        url: getQueryStringWithCreator(
          `b/creatorDeliverableFileUpload?${new URLSearchParams(
            queryParams
          ).toString()}`
        ),
      }),
    }),
    startMultipartFileUpload: builder.query({
      query: (queryParams) => ({
        url: getQueryStringWithCreator(
          `b/startMultipartFileUpload?${new URLSearchParams(
            queryParams
          ).toString()}`
        ),
      }),
    }),
    generateMultipartPresignedUrl: builder.query({
      query: (queryParams) => ({
        url: getQueryStringWithCreator(
          `b/generatePresignedUrl?${new URLSearchParams(
            queryParams
          ).toString()}`
        ),
      }),
    }),
    completeMultipartUpload: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`b/completeMultipartUpload`),
        method: "POST",
        body,
      }),
    }),
    submitDraftForReview: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`b/submitDraft`),
        method: "POST",
        body,
      }),
    }),
    getCollabProducts: builder.query({
      query: ({ id, search, sort_by }) => ({
        url: getQueryStringWithCreator(
          `b/creatorVariantProduct/${id}?search=${search}&sort_by=${sort_by}`
        ),
      }),
    }),
    uploadVoiceNote: builder.mutation({
      query: ({ formData }) => ({
        url: getQueryStringWithCreator("b/uploadVoiceNote"),
        method: "POST",
        body: formData,
      }),
    }),
    checkPostExists: builder.mutation({
      query: ({ url }) => ({
        url: getQueryStringWithCreator("b/checkPostExists"),
        method: "POST",
        body: {
          url,
        },
      }),
      transformResponse: (res) => res?.data || res,
    }),
    addToCart: builder.mutation({
      query: ({ id, body }) => ({
        url: getQueryStringWithCreator(`b/productCart/${id}`),
        method: "POST",
        body,
      }),
    }),
    getCart: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`b/productCart/${id}`),
      }),
      transformResponse: (res) => res?.data || [],
    }),
    updateCart: builder.mutation({
      query: ({ id, body }) => ({
        url: getQueryStringWithCreator(`b/UpdateProductCart/${id}`),
        method: "PUT",
        body,
      }),
    }),
    placeOrder: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`b/createSourcingOrder`),
        method: "POST",
        body,
      }),
    }),
    updateCreatorFeedback: builder.mutation({
      query: ({ id, feedback_note, feedback_rating }) => ({
        url: `/b/updateCreatorFeedback/${id}`,
        method: "PATCH",
        body: postQueryBodyWithCreator({ feedback_note, feedback_rating }),
      }),
      invalidatesTags: ["Feedback"],
    }),
    markVariantVisited: builder.mutation({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`b/markVariantVisited/${id}`),
        method: "PUT",
      }),
    }),
    getSuggestedActions: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(
          `c/suggestedActions/getSuggestedActions/`
        ),
      }),
      transformResponse: (res) => res?.data,
      providesTags: ["SuggestedActions"],
    }),
    dismissSuggestedAction: builder.mutation({
      query: (body) => ({
        url: `c/suggestedActions/dismissSuggestedAction/`,
        method: "POST",
        body,
      }),
      transformResponse: (res) => res?.data,
    }),
    markCSMCallbackInitiated: builder.mutation({
      query: (body) => ({
        url: `c/suggestedActions/markCSMCallbackInitiated/`,
        method: "POST",
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["SuggestedActions"],
    }),
    getFaqHomePage: builder.query({
      query: ({ onBoarded }) => ({
        url: getQueryStringWithCreator(
          `c/faq/homePage?on_boarded_cohort=${onBoarded}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    getCategoryQuestions: builder.query({
      query: ({ categoryId }) => ({
        url: getQueryStringWithCreator(
          `c/faq/categoryQuestions?category_id=${categoryId}`
        ),
      }),
      transformResponse: (res) => res?.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { categoryId } = queryArgs;
        return `endpointName=${endpointName}&categoryId=${categoryId}`;
      },
    }),
    getFaqCategoryQuestionAnswer: builder.query({
      query: ({ categoryId, questionId }) => ({
        url: getQueryStringWithCreator(
          `c/faq/categoryQuestionAnswer?category_id=${categoryId}&question_id=${questionId}`
        ),
      }),
      transformResponse: (res) => res?.data,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { categoryId, questionId } = queryArgs;
        return `endpointName=${endpointName}&categoryId=${categoryId}&questionId=${questionId}`;
      },
    }),
    getFaqQuestionsSearch: builder.query({
      query: ({ query, offset }) => ({
        url: getQueryStringWithCreator(
          `c/faq/search?query=${query}&limit=10&offset=${offset}`
        ),
      }),
      transformResponse: (res) => res?.data,
    }),
    rearrangeProducts: builder.mutation({
      query: (body) => ({
        url: "c/rearrangeProducts",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getCreatorRecentSearch: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/faq/creatorRecentSearch`),
      }),
      transformResponse: (res) => res?.data,
    }),
    addCreatorRecentSearchFaq: builder.mutation({
      query: (body) => ({
        url: "c/faq/addCreatorRecentSearch",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getAllGrowthRecommendedProduct: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getGrowthRecommendationProducts`),
      }),
      transformResponse: (res) => {
        return {
          data: { data: res?.data, timeLeft: res?.days_till_next_generation },
        };
      },
      providesTags: ["AllGrowthRecommendationProducts"],
    }),
    getAllGrowthSimilarRecommendedProduct: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(
          `c/getGrowthRecommendationSimilarProducts`
        ),
      }),
      transformResponse: (res) => {
        return {
          data: { data: res?.data, timeLeft: res?.days_till_next_generation },
        };
      },
      providesTags: ["AllGrowthSimilarRecommendedProduct"],
    }),
    getAllGrowthRealisedProduct: builder.query({
      query: ({ sortBy = "default" }) => ({
        url: getQueryStringWithCreator(
          `c/getGrowthRealizedProducts?sort_by=${sortBy}`
        ),
      }),
      transformResponse: (res) => res?.data,
      providesTags: ["AllGrowthRealisedProducts"],
    }),
    optinToGrowthRecommendation: builder.mutation({
      query: ({ id, discoverCopyOrigin }) => ({
        url: getQueryStringWithCreator(`c/growthRecommendationOptIn/${id}`),
        body: postQueryBodyWithCreator({
          ...(discoverCopyOrigin
            ? { discover_copy_origin: discoverCopyOrigin }
            : {}),
        }),
        method: "PUT",
      }),
    }),
    createProductForGrowthSimilarProductRecommendation: builder.mutation({
      query: ({ id, discoverCopyOrigin }) => ({
        url: getQueryStringWithCreator(
          `c/mapOrOptGrowthRecommendationSimilarProducts/${id}`
        ),
        body: postQueryBodyWithCreator({
          ...(discoverCopyOrigin
            ? { discover_copy_origin: discoverCopyOrigin }
            : {}),
        }),
        method: "POST",
      }),
    }),
    triggerAutoConentCreation: builder.mutation({
      query: (body) => ({
        url: getQueryStringWithCreator(`cutouts/contentGen`),
        body: postQueryBodyWithCreator(body),
        method: "POST",
        invalidatesTags: ["AutoContent"],
      }),
    }),
    getAutoContentList: builder.query({
      query: ({ template, page = 1, limit = 5 }) => ({
        url: getQueryStringWithCreator(
          `cutouts/getContentListData?template=${template}&page=${page}&limit=${limit}`
        ),
        method: "GET",
      }),
      transformResponse: (response) => {
        return {
          data: {
            content_data: response?.data?.content_data || [],
            has_more: response?.data?.has_more || false,
            current_page: response?.data?.current_page || 1,
          },
          pages: [response?.data?.content_data] || [],
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `endpointName=${endpointName}?template=${queryArgs.template}`;
      },
      merge: (currentCache, newItems) => {
        if (!currentCache?.pages) {
          currentCache = { pages: [] };
        }
        if (newItems.data.current_page === 1) {
          currentCache.pages = [];
        }
        currentCache.pages[newItems.data.current_page - 1] =
          newItems.data.content_data;
        currentCache.data = newItems.data;

        return currentCache;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: ["AutoContent"],
    }),
    getBrandInterfaceData: builder.mutation({
      query: ({ brand_id, product_id }) => ({
        url: `c/getBrandInterfaceData`,
        body: postQueryBodyWithCreator({ brand_id, product_id }),
        method: "POST",
      }),
      transformResponse: (res) => res?.data,
    }),
    getBrandBucketInfo: builder.query({
      query: ({ brand_id }) => {
        if (!brand_id) {
          throw new Error("brand_id must be provided");
        }
        return {
          url: `c/getBrandBucketInfo?brand_id=${encodeURIComponent(brand_id)}`,
        };
      },
      transformResponse: (res) => res?.data,
    }),
    handleEngageConnectionConfirmation: builder.mutation({
      query: () => ({
        url: `c/handleEngageConnectionConfirmation`,
        body: postQueryBodyWithCreator({}),
        method: "POST",
      }),
      invalidatesTags: ["EngageListingMeta"],
    }),
    handleFacebookEngageConfirmation: builder.mutation({
      query: () => ({
        url: `c/handleFacebookEngageConfirmation`,
        body: postQueryBodyWithCreator({}),
        method: "POST",
      }),
      invalidatesTags: ["EngageListingMeta"],
    }),
    getTDSCertificate: builder.query({
      query: ({ financialYear, quarter }) => ({
        url: getQueryStringWithCreator(
          `c/getCreatorTDSCertificate?financial_year=${financialYear}&quarter=${quarter}`
        ),
        method: "GET",
        transformResponse: (res) => res?.data,
        providesTags: ["TDSCertificateData"],
      }),
    }),
    getTDSCertificateSummary: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getCreatorTDSCertificateSummary`),
        method: "GET",
      }),
      providesTags: ["TDSCertificateSummary"],
    }),
    mailTDSCertificate: builder.query({
      query: ({ certificate_id }) => ({
        url: getQueryStringWithCreator(
          `c/emailCreatorTDSCertificate?certificate_id=${certificate_id}`
        ),
        method: "GET",
        transformResponse: (res) => res?.data,
        providesTags: ["MailTDSCertificate"],
      }),
    }),
    frontEndLogger: builder.mutation({
      query: ({ log, type }) => ({
        url: "frontEndLogger",
        body: {
          log: log,
          type: type,
        },
        method: "POST",
        invalidatesTags: ["frontEndLogger"],
      }),
    }),
    getAllRewardsHistory: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/getAllRewardsHistory`),
      }),
      transformResponse: (res) => res?.data,
    }),
    shoppingAppFilterLabels: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/shoppingAppFilterLabels`),
      }),
    }),
    fetchShoppingAppMetrics: builder.query({
      query: (params) => ({
        url: getQueryStringWithCreator(`c/fetchShoppingAppMetrics`),
        params,
      }),
      transformResponse: (res) => res?.data,
    }),
    verifyPan: builder.mutation({
      query: ({ pan, bankName }) => ({
        url: `c/accountVerificationFlow?verify=pan`,
        method: "POST",
        body: postQueryBodyWithCreator({
          pan,
          bankName,
        }),
      }),
      invalidatesTags: (result, error, { pan }) => [
        { type: "PanBankPoll", id: `pan-${(pan || "").trim()}` },
        { type: "PanBankPoll", id: "LIST" },
      ],
    }),
    verifyBank: builder.mutation({
      query: ({ accountNumber, ifsc, bankName, pan }) => ({
        url: `c/accountVerificationFlow?verify=bank`,
        method: "POST",
        body: postQueryBodyWithCreator({
          accountNumber,
          ifsc,
          bankName,
        }),
      }),
      invalidatesTags: (result, error, arg) => [
        {
          type: "PanBankPoll",
          id: `bank-${(arg.pan || "").trim()}-${(
            arg.accountNumber || ""
          ).trim()}-${(arg.ifsc || "").trim()}-${(arg.bankName || "").trim()}`,
        },
        { type: "PanBankPoll", id: "LIST" },
      ],
    }),
    pollPanStatus: builder.query({
      query: ({ pan }) => ({
        url: getQueryStringWithCreator(`c/pollPanStatus`) + `&pan=${pan}`,
        method: "GET",
      }),
      providesTags: (result, error, { pan }) => [
        { type: "PanBankPoll", id: `pan-${(pan || "").trim()}` },
        { type: "PanBankPoll", id: "LIST" },
      ],
      // Don't cache the response
      keepUnusedDataFor: 0,
      // Force refetch when component mounts
      refetchOnMountOrArgChange: true,
      // Continue polling until we get a success response or hit timeout
      pollingInterval: (data, queryState) => {
        // If we have data and it's successful, stop polling
        if (data?.success) {
          return 0; // Stop polling
        }
        // If we've been polling for more than 30 seconds, stop
        if (
          queryState.startedTimeStamp &&
          Date.now() - queryState.startedTimeStamp > 30000
        ) {
          return 0; // Stop polling
        }
        return 2000; // Continue polling every 2 seconds
      },
    }),
    pollBankStatus: builder.query({
      query: ({ pan, accountNumber, bankName, ifsc }) => ({
        url:
          getQueryStringWithCreator(`c/pollBankStatus`) +
          `&pan=${pan}` +
          `&accountNumber=${accountNumber}` +
          `&bankName=${bankName}` +
          `&ifsc=${ifsc}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => [
        {
          type: "PanBankPoll",
          id: `bank-${(arg.pan || "").trim()}-${(
            arg.accountNumber || ""
          ).trim()}-${(arg.ifsc || "").trim()}-${(arg.bankName || "").trim()}`,
        },
        { type: "PanBankPoll", id: "LIST" },
      ],
      // Don't cache the response
      keepUnusedDataFor: 0,
      // Force refetch when component mounts
      refetchOnMountOrArgChange: true,
      // Continue polling until we get a success response or hit timeout
      pollingInterval: (data, queryState) => {
        // If we have data and it's successful, stop polling
        if (data?.success) {
          return 0; // Stop polling
        }
        // If we've been polling for more than 30 seconds, stop
        if (
          queryState.startedTimeStamp &&
          Date.now() - queryState.startedTimeStamp > 30000
        ) {
          return 0; // Stop polling
        }
        return 2000; // Continue polling every 2 seconds
      },
    }),
    verifyRazorpay: builder.mutation({
      query: ({ accountNumber, ifsc, bankName, email, phoneNumber }) => ({
        url: `c/accountVerificationFlow?verify=razorpay`,
        method: "POST",
        body: postQueryBodyWithCreator({
          accountNumber,
          ifsc,
          bankName,
          email,
          phoneNumber,
        }),
      }),
    }),
    pollRazorpayStatus: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/pollRazorpayStatus`),
        method: "GET",
      }),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      pollingInterval: (data, queryState) => {
        if (data?.success) {
          return 0;
        }
        if (
          queryState.startedTimeStamp &&
          Date.now() - queryState.startedTimeStamp > 60000
        ) {
          return 0;
        }
        return 2000;
      },
    }),
    updateCreatorBrandAffiliation: builder.mutation({
      query: (body) => ({
        url: `c/updateCreatorBrandAffiliation`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["BrandAffiliation"],
    }),
    getCreatorBrandAffiliation: builder.query({
      query: ({ brand_id }) => ({
        url:
          getQueryStringWithCreator(`c/getCreatorBrandAffiliation`) +
          `&brand_id=${brand_id}`,
        method: "GET",
      }),
      providesTags: ["BrandAffiliation"],
    }),
    ftuxViewed: builder.mutation({
      query: (body) => ({
        url: "c/setMilestoneAchieved",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getWhatsNew: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/announcements/summary`),
      }),
    }),
    getWhatsNewCategories: builder.query({
      query: () => ({
        url: `c/announcements/categories`,
      }),
    }),
    getWhatsNewDetails: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`c/announcements/${id}`),
      }),
    }),
    getWhatsNewFeedback: builder.query({
      query: ({ id }) => ({
        url: getQueryStringWithCreator(`c/announcements/${id}/feedback/`),
      }),
    }),
    updateWhatsNewFeedback: builder.mutation({
      query: ({ id, feedback_rating }) => ({
        url: `c/announcements/${id}/feedback/`,
        method: "POST",
        body: postQueryBodyWithCreator({ feedback_rating }),
      }),
    }),
    markWhatsNewAsRead: builder.mutation({
      query: ({ id, mark_as }) => ({
        url: `c/announcements/${id}/toggle_read/`,
        method: "POST",
        body: postQueryBodyWithCreator({ mark_as }),
      }),
    }),
    creatorGSTDetails: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/creatorGSTCertificate`),
        method: "GET",
      }),
      transformResponse: (res) => res,
    }),
    uploadGSTFile: builder.mutation({
      query: ({ file, gst_number }) => {
        const formData = new FormData();
        if (file) formData.append("certificate_file", file);
        if (gst_number) formData.append("gst_number", gst_number);
        return {
          url: getQueryStringWithCreator(`c/creatorGSTCertificate`),
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (res) => res,
    }),
    createTDSInvoice: builder.mutation({
      query: (body) => {
        return {
          url: getQueryStringWithCreator(`c/creatorTDSInvoice`),
          method: "POST",
          body: postQueryBodyWithCreator(body),
        };
      },
      transformResponse: (res) => res,
    }),
    getTDSInvoice: builder.query({
      query: (queryParams) => {
        return {
          url: getQueryStringWithCreator(
            `c/creatorTDSInvoice?${new URLSearchParams(queryParams).toString()}`
          ),
          method: "GET",
        };
      },
      transformResponse: (res) => res,
    }),
    claimCreatorGST: builder.mutation({
      query: (body) => {
        return {
          url: getQueryStringWithCreator(`c/creatorGSTClaim`),
          method: "POST",
          body: postQueryBodyWithCreator(body),
        };
      },
      transformResponse: (res) => res,
    }),
    createGSTInvoice: builder.mutation({
      query: (body) => {
        return {
          url: getQueryStringWithCreator(`c/createGSTInvoice`),
          method: "POST",
          body: postQueryBodyWithCreator(body),
        };
      },
      transformResponse: (res) => res,
    }),
    creatorTDSInvoiceMail: builder.mutation({
      query: (body) => {
        return {
          url: getQueryStringWithCreator(`c/sendTDSCertificateEmail`),
          method: "POST",
          body: postQueryBodyWithCreator(body),
        };
      },
    }),
    mergeAndUploadInvoice: builder.mutation({
      query: (body) => {
        return {
          url: getQueryStringWithCreator(`c/mergeAndUploadInvoice`),
          method: "POST",
          body: postQueryBodyWithCreator(body),
        };
      },
      transformResponse: (res) => res,
    }),
    handlePlotlineEventsData: builder.mutation({
      query: (body) => ({
        url: `handlePlotlineEventsData`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getStoryBannerStatus: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/storyBannerStatus`),
      }),
      transformResponse: (res) => res?.data,
      providesTags: ["StoryBannerStatus"],
    }),
    updateStoryBannerStatus: builder.mutation({
      query: (body) => ({
        url: `c/storyBannerStatus`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["StoryBannerStatus"],
    }),
    getEngageSettingsPageView: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`c/engageSettingsPageView`),
      }),
    }),
    updateEngageSettingsPageView: builder.mutation({
      query: (body) => ({
        url: `c/engageSettingsPageView`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    markEngageListingSeen: builder.mutation({
      query: (body = {}) => ({
        url: `c/markEngageListingSeen`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["QuickAccessIndicators", "EngageListingMeta"],
    }),
    getChatThread: builder.query({
      query: ({ page = 1, page_size = 10, campaign_status_list }) => {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(page_size),
        });

        if (campaign_status_list) {
          params.set("campaign_status_list", campaign_status_list);
        }

        return {
          url: getQueryStringWithCreator(
            `c/chat/getChatThreads?${params.toString()}`
          ),
          method: "GET",
        };
      },
    }),
    getScriptSubmissions: builder.query({
      query: ({ collabId }) => ({
        url: getQueryStringWithCreator(`b/scriptSubmissions/${collabId}`),
        method: "GET",
      }),
      providesTags: ["ScriptSubmissions"],
      transformResponse: (res) => res?.data,
    }),
    postScriptSubmission: builder.mutation({
      query: ({ collabId, scriptData }) => ({
        url: getQueryStringWithCreator(`b/scriptSubmissions/${collabId}`),
        method: "POST",
        body: postQueryBodyWithCreator(scriptData),
      }),
      invalidatesTags: ["ScriptSubmissions"],
    }),
    getThreadMessages: builder.query({
      query: ({ threadId, page_size = 10, id }) => {
        let queryString = `c/chat/getThreadMessages/?thread_id=${threadId}&page_size=${page_size}`;

        // Add id parameter for cursor-based pagination
        if (id) {
          queryString += `&id=${id}`;
        }

        return {
          url: getQueryStringWithCreator(queryString),
          method: "GET",
        };
      },
    }),
    sendMessage: builder.mutation({
      query: ({ threadId, message, media_type, media_url }) => ({
        url: getQueryStringWithCreator(`c/chat/sendMessage/${threadId}/`),
        method: "POST",
        body: { message, media_type, media_url },
      }),
    }),
    submitAdsCode: builder.mutation({
      query: ({ adsData }) => ({
        url: getQueryStringWithCreator(`b/submitAdsCode`),
        method: "PUT",
        body: postQueryBodyWithCreator(adsData),
      }),
      invalidatesTags: ["Campaign"],
    }),
    chatMessageUpdate: builder.mutation({
      query: ({ chatId, body }) => ({
        url: getQueryStringWithCreator(`c/chat/chatMessageUpdate/${chatId}/`),
        method: "PUT",
        body,
      }),
    }),
    getChatUnreadMessageCount: builder.query({
      query: () => ({
        url: getQueryStringWithCreator(`b/getChatUnreadMessageCount`),
        method: "GET",
      }),
    }),
    uploadStoryScreenshot: builder.mutation({
      query: ({ formData }) => ({
        url: getQueryStringWithCreator("b/uploadStoryScreenshot"),
        method: "POST",
        body: formData,
      }),
    }),
    getCustomEngageMediaUploadUrl: builder.mutation({
      query: ({ fileName, contentType }) => ({
        url: getQueryStringWithCreator("c/getCustomEngageMediaUploadUrl"),
        method: "POST",
        body: { file_name: fileName, content_type: contentType },
      }),
    }),
    getChatReferenceLocation: builder.query({
      query: (chatId) => ({
        url: getQueryStringWithCreator(
          `c/chat/getChatReferenceLocation/${chatId}/`
        ),
        method: "GET",
      }),
    }),
    getCreatorThreadUnreadMessageCount: builder.query({
      query: ({ thread_id }) => ({
        url: getQueryStringWithCreator(
          `b/getCreatorThreadUnreadMessageCount/${thread_id}`
        ),
        method: "GET",
      }),
    }),
    getIgStoryDrawerStatus: builder.query({
      query: ({ creator }) =>
        getQueryStringWithCreator(`c/igStoryDrawerStatus?creator=${creator}`),
      providesTags: ["IgStoryDrawerStatus"],
    }),
    updateIgStoryDrawerStatus: builder.mutation({
      query: ({ creator, hasInteractedIgStoryDrawer }) => ({
        url: "c/igStoryDrawerStatus",
        method: "POST",
        body: postQueryBodyWithCreator({
          creator,
          ...(hasInteractedIgStoryDrawer !== undefined && {
            hasInteractedIgStoryDrawer,
          }),
        }),
      }),
      invalidatesTags: ["IgStoryDrawerStatus"],
    }),

    // ── Folders ──
    getFolders: builder.query({
      query: () => getQueryStringWithCreator("c/folders/"),
      providesTags: ["Folders"],
      transformResponse: (res) => (res?.success ? res.data : []),
    }),
    createFolder: builder.mutation({
      query: ({ name }) => ({
        url: "c/folders/",
        method: "POST",
        body: postQueryBodyWithCreator({ name }),
      }),
      invalidatesTags: ["Folders"],
    }),
    deleteFolder: builder.mutation({
      query: ({ folderId }) => ({
        url: `c/folders/${folderId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Folders"],
    }),
    getFolderItems: builder.query({
      query: ({ folderId }) =>
        getQueryStringWithCreator(`c/folders/${folderId}/items/`),
      providesTags: (_res, _err, { folderId }) => [
        { type: "FolderItems", id: folderId },
      ],
      transformResponse: (res) => (res?.success ? res.data : []),
    }),
    addLinkToFolder: builder.mutation({
      query: ({ folderId, link, client_og_data }) => ({
        url: `c/folders/${folderId}/items/`,
        method: "POST",
        body: postQueryBodyWithCreator({
          link,
          ...(client_og_data && {
            client_og_data: trimClientOgData(client_og_data),
          }),
        }),
      }),
      invalidatesTags: (_res, _err, { folderId }) => [
        { type: "FolderItems", id: folderId },
        "Folders",
      ],
    }),
    addLinkToFolderQuick: builder.mutation({
      query: ({ link, folder_id, source, client_og_data }) => ({
        url: "c/folders/addLink/",
        method: "POST",
        body: postQueryBodyWithCreator({
          link,
          folder_id,
          ...(source ? { source } : {}),
          ...(client_og_data && {
            client_og_data: trimClientOgData(client_og_data),
          }),
        }),
      }),
      invalidatesTags: ["Folders", "FolderItems"],
    }),
    deleteFolderItem: builder.mutation({
      query: ({ folderId, itemId }) => ({
        url: `c/folders/${folderId}/items/${itemId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { folderId }) => [
        { type: "FolderItems", id: folderId },
        "Folders",
      ],
    }),
    getAgentConversations: builder.query({
      query: ({ creatorUsername, beforeId, limit = 20 }) => {
        let url = `agent/conversations/${creatorUsername}/`;
        const params = [];
        if (limit) params.push(`limit=${limit}`);
        if (beforeId) params.push(`before_id=${beforeId}`);
        if (params.length) url += `?${params.join("&")}`;
        return { url, method: "GET" };
      },
    }),
    createAgentConversation: builder.mutation({
      query: ({ creatorUsername, title = "" }) => ({
        url: `agent/conversations/${creatorUsername}/`,
        method: "POST",
        body: { title },
      }),
    }),
    getAgentConversation: builder.query({
      query: ({ creatorUsername, conversationId }) => ({
        url: `agent/conversations/${creatorUsername}/${conversationId}/`,
        method: "GET",
      }),
    }),
    getAgentConversationMessages: builder.query({
      query: ({ creatorUsername, conversationId, beforeId, limit = 20 }) => {
        let url = `agent/conversations/${creatorUsername}/${conversationId}/messages/`;
        const params = [];
        if (limit) params.push(`limit=${limit}`);
        if (beforeId) params.push(`before_id=${beforeId}`);
        if (params.length) url += `?${params.join("&")}`;
        return { url, method: "GET" };
      },
    }),
    updateAgentConversationTitle: builder.mutation({
      query: ({ creatorUsername, conversationId, title }) => ({
        url: `agent/conversations/${creatorUsername}/${conversationId}/`,
        method: "PATCH",
        body: { title },
      }),
    }),
    deleteAgentConversation: builder.mutation({
      query: ({ creatorUsername, conversationId }) => ({
        url: `agent/conversations/${creatorUsername}/${conversationId}/`,
        method: "DELETE",
      }),
    }),
    getCOCEventData: builder.query({
      query: () => getQueryStringWithCreator("c/getCOCEventData"),
      extraOptions: { maxRetries: 1 },
      providesTags: ["COCEvent"],
      transformResponse: (response) => {
        if (response?.success) {
          return response.data;
        }
        return null;
      },
    }),

    // ── IG Post Product Suggestions ──
    getIGPostSuggestion: builder.query({
      query: ({ shortCode }) =>
        getQueryStringWithCreator(
          `c/getIGPostSuggestion?ig_post_short_code=${shortCode}`
        ),
      providesTags: (_res, _err, { shortCode }) => [
        { type: "IGPostSuggestion", id: shortCode },
      ],
      transformResponse: (response) =>
        response?.success ? response.data : null,
    }),
    approveIGPostSuggestion: builder.mutation({
      query: ({ shortCode, approvedSuggestionIds, manualProductUrls }) => ({
        url: "c/approveIGPostSuggestion",
        method: "POST",
        body: postQueryBodyWithCreator({
          ig_post_short_code: shortCode,
          approved_suggestion_ids: approvedSuggestionIds || [],
          // Each item: { url: string, added_via_catalog?: number (folder_item_id) }
          manual_product_urls: manualProductUrls || [],
        }),
      }),
      invalidatesTags: (result, error, { shortCode }) => [
        { type: "IGPostSuggestion", id: shortCode },
      ],
    }),
    rejectIGPostSuggestion: builder.mutation({
      query: ({ shortCode }) => ({
        url: "c/rejectIGPostSuggestion",
        method: "POST",
        body: postQueryBodyWithCreator({ ig_post_short_code: shortCode }),
      }),
      invalidatesTags: (result, error, { shortCode }) => [
        { type: "IGPostSuggestion", id: shortCode },
      ],
    }),
  }),
});
export const {
  useGetCreatorDataQuery,
  useLazyGetCreatorDataQuery,
  useLazyGetStorePostsDataQuery,
  useLazyGetStoreCollectionsDataQuery,
  useLazyGetStoreProductsDataQuery,
  useLazyGetStoreDraftsDataQuery,
  useLazyGetCreatorShopCountsQuery,
  useLazyGetPostProductsDetailsQuery,
  useLazyGetCreatorTagsQuery,
  usePostNewPostDataMutation,
  useLazyGetCreatorPostDataQuery,
  useGetCreatorPostDataQuery,
  useLazyGetProductLinksDataQuery,
  useGetProductLinksDataQuery,
  usePostShopProductsDataMutation,
  useGetDiyConfigQuery,
  useAutoScrapeProductMutation,
  useFinalizeProductsMutation,
  useUpdatePostOrCollectionStatusMutation,
  usePostNewCollectionDataMutation,
  usePostYoutubeLoginMutation,
  usePostSendOTPMutation,
  usePostVerifyOTPMutation,
  usePostLogOTPAuthStatusMutation,
  usePostFirebaseLoginMutation,
  useUpdateProductStatusMutation,
  useLazyGetSessionIdQuery,
  useLazyGetYoutubePostsListQuery,
  useLazyGetInstaPostsListQuery,
  useLazyGetInstaPostsListExplicitQuery,
  useLazyGetInstaStoriesListQuery,
  useLazyGetFacebookPostsListQuery,
  useLazyGetCreatorSocialLoginStatusQuery,
  usePostSocialManualLoginMutation,
  usePostInstagramLoginMutation,
  usePostPinterestLoginMutation,
  usePostSnapchatLoginMutation,
  useLazyGetCrossPostingConfigQuery,
  useLazyGetCrossPostAndCommissionsQuery,
  useLazyAllowedCrossPostingPlatformsQuery,
  useLazyCrossPostStatusOnShopQuery,
  useDismissFailedCrossPostBannerMutation,
  useLazyCrossPostedPlatformsForPostQuery,
  useTriggerCrossPostMutation,
  useLazyGetTokenViaAuthCodeQuery,
  usePostCrossPostingConfigMutation,
  usePostConfirmCreatorUsernameMutation,
  useLazyGetPostOrCollectionProductsDataQuery,
  usePostFilteredBrandsDataMutation,
  useLazyFetchBrandTagsDataQuery,
  useLazyGetOauthClientNameQuery,
  useLazyGetFiltersAndSortDataQuery,
  useLazyFetchAllBrandBannersDataQuery,
  useGetFrameFiltersDataQuery,
  useLazyGetPostRedirectsQuery,
  useLazyGetCollectionRedirectsQuery,
  useLazyGetProductRedirectsQuery,
  usePostSingleProductLinkMutation,
  useLazyCheckTaskStatusQuery,
  useUpdateCreatorProfileMutation,
  useLazyFetchCreatorBankDetailsQuery,
  useFetchCreatorBankDetailsQuery,
  useSubmitCreatorBankDetailsMutation,
  useLazyGetCTKDetailsQuery,
  useLazyGetCreatorTemplatesQuery,
  useUpdateCreatorTemplatesMutation,
  useLoginMutation,
  useLazyGetCreatorSalesDataQuery,
  useGetCreatorSalesDataQuery,
  useLazyGetCreatorAnalyticsChartDataQuery,
  useLazyGetAmazonDataQuery,
  useLazyGetBrandWiseEarningsSplitDataQuery,
  useLazyGetChannelWiseEarningsSplitDataQuery,
  useLazyGetCreatorAnalyticsPostsDataQuery,
  useLazyGetCreatorAnalyticsCollectionsDataQuery,
  useLazyGetCreatorAnalyticsProductsDataQuery,
  useLazyGetCreatorAnalyticsRedirectionsDataQuery,
  useLazyGetCreatorLifetimeEarningsQuery,
  useLazyGetCreatorPastPayoutsQuery,
  useDisconnectSnapchatMutation,
  useDisconnectFacebookMutation,
  useConnectFacebookMutation,
  useUpdateCreatorDashboardMilestonesStatusMutation,
  useLazyGetMarketingMediaReelsQuery,
  useLazyGetCreatorsUsernamesQuery,
  useLazyGetCalendarPostsReleaseDatesQuery,
  useLazyGetCreatorRewardsAnalyticsQuery,
  useLazyGetPostOrCollectionLifetimeFunnelQuery,
  useLazyGetPostOrCollectionEarningsQuery,
  useLazyGetPostOrCollectionProductEarningsQuery,
  useLazyGetSortAndFiltersForAnalyticsQuery,
  useUpdateCTKsettingsMutation,
  useLazyGetInstaBasicProfileDataQuery,
  useLazyGetProductAnalyticsQuery,
  useLazyGetShopProductsRedirectsQuery,
  useLazyGetCampaignDetailsQuery,
  useLazyGetMeeshoQrQuery,
  useLazyGetBrandRewardsQuery,
  useLazyGetRewardsLeaderboardQuery,
  useLazyGetRewardsSummaryQuery,
  useGetQuickAccessIndicatorsQuery,
  useGetRewardsListingMetaQuery,
  useGetEngageListingMetaQuery,
  useGetStreaksListingMetaQuery,
  useMarkStreaksTabSeenMutation,
  useMarkStreakMainTabSeenMutation,
  useCollectCreatorFeedbackMutation,
  useGetMeeshoCatalogQuery,
  useLazyGetDateWiseOrderSummaryQuery,
  useLazyGetOrderHistoryForDateQuery,
  useLazyFetchCreatorAddressQuery,
  useFetchCreatorAddressQuery,
  useUpdateCreatorAddressMutation,
  useLazyFetchAddressByIdQuery,
  useCreateNewAddressMutation,
  useLazyGetPostOrCollectionLifetimeLinkClicksQuery,
  useDeleteWishlinkAccountRequestMutation,
  useLazyDeactivateWishlinkAccountQuery,
  useLazyGetNotificationsQuery,
  useLazyGetEarlierNotificationsQuery,
  useLazyGetBellIconStatusQuery,
  useMarkNotificationAsReadMutation,
  useLazyGetCreatorReferralEarningsQuery,
  useLazyGetRefererDetailsQuery,
  useLazyGetCreatorReferralLedgerQuery,
  useLazyGetCreatorReferralDataQuery,
  useUpdateRewardsFocusMutation,
  useUpdateCreatorDemoDetailsMutation,
  useUpdateCreatorBPCMeetDetailsMutation,
  useLazyGetScrapingFailureDomainsQuery,
  useLazyGetMarqueeDetailsQuery,
  useGetExploreBannerDetailsQuery,
  useLazyFetchCreatorTypeQuery,
  useLazyGetOverlaysQuery,
  useGetOverlaysQuery,
  useUpdateFirstLinkSkippedMutation,
  useUpdateOverlayStatusAsReadMutation,
  useResetOverlayStatusMutation,
  useUpdateEngageGtmFlagMutation,
  useUpdatePayoutsGtmFlagMutation,
  useUpdateAppUtmMutation,
  useGetCommissionsDetailsQuery,
  useGetCommissionsDetailsByBrandIdQuery,
  useGetCurrentPayoutSummaryQuery,
  useGetPayoutStatusCardsQuery,
  useLazyGetPayoutStatusCardsQuery,
  useGetPastPayoutListQuery,
  useLazyGetPastPayoutListQuery,
  useGetPastPayoutListV2Query,
  useGetCreatorLifetimeEarningsV2Query,
  useGetSinglePastPayoutDetailsQuery,
  useGetSinglePastPayoutDetailsV2Query,
  useLazyGetCommissionsBrandDetailsQuery,
  useLazyGetCommissionsDateWiseOrderLedgerQuery,
  useLazyGetCommissionsSingleDateOrderLedgerQuery,
  useLazyGetPastPayoutBrandDetailsQuery,
  useSubmitCreatorReturnsRequestMutation,
  useGetPendingPayoutQuery,
  useGetCategoryWiseBrandCommissionQuery,
  useUpdateGtmAsCompletedMutation,
  useLazyGetNykaaAprilRewardsQuery,
  useSubmitCreatorFreshdeskRestoreIdMutation,
  useSubmitCreatorFreshdeskAttributesMutation,
  useGetAllRewardsQuery,
  useGetAllRewardsPaginatedQuery,
  useLazyGetAllRewardsPaginatedQuery,
  useGetAllRewardsLiteQuery,
  useGetSpecificRewardDetailsQuery,
  useGetWishlinkRewardDetailsQuery,
  useGetRewardEligibleProductsQuery,
  useGetRewardSourcedProductsQuery,
  useLazyGetRewardRelatedPostsQuery,
  useGetRewardRelatedPostsQuery,
  useUpdateRewardAccordionStateMutation,
  useGetLeaderboardRewardAmountQuery,
  useLazyGetLeaderboardRewardAmountQuery,
  useGetLeaderboardRanksQuery,
  useLazyGetRewardDetailsQuery,
  useOptInRewardMutation,
  useMarkRewardsListingSeenMutation,
  useMarkSourcingListingSeenMutation,
  useMarkSourcingOrdersSeenMutation,
  useMarkSourcingEntitySeenMutation,
  useMarkRewardDetailSeenMutation,
  useMarkRewardSeenMutation,
  useMarkSelectByWishlinkListingSeenMutation,
  useMarkGrowthListingSeenMutation,
  useMarkQuickAccessIndicatorSeenMutation,
  useGetTotalViewsForCreatorQuery,
  useFetchCreatorConfirmedNudgeDetailsQuery,
  useFetchCreatorBrandConfirmedNudgeDetailsQuery,
  useMarkPayoutSeenMutation,
  useGetCreatorRewardMilestoneDataForBrandQuery,
  useGetCreatorRewardMilestoneProductsDataQuery,
  useWebengageEventTriggerMutation,
  useGetCommissionFiltersQuery,
  useGetMonthYearListSincePayoutV2Query,
  useGetPendingConfirmedCommissionSplitQuery,
  useGetConfirmedCommissionSplitInPayoutsQuery,
  useGetSimilarProductRecommendationsQuery,
  useEngageDebuggerReportMutation,
  useGetEngageDebuggerReportQuery,
  useGetAllBrandDealsQuery,
  useGetAllBrandDealCollectionQuery,
  useGetBpcCollectionQuery,
  useGetBrandDealDetailQuery,
  useGetRecommendedPostsForGrowthQuery,
  useGetRecommendedProductsForPostQuery,
  useGetBrandTopOfferQuery,
  useGetBrandBucketInfoQuery,
  useGetD2CCollectionQuery,
  useGetD2CCollectionRetrieveQuery,
  useGetRewardExploreBannerQuery,
  useGetContentInspirationsByIdsQuery,
  useGetHomeTopEcommerceQuery,
  useBrandExploreBannersRetrieveByIdQuery,
  useLazySetCreatorFestiveBannerFlagQuery,
  useGetAllCreatorPostFramesQuery,
  useGetCreatorPostFrameDetailsQuery,
  useGetFaqHomePageQuery,
  useGetCategoryQuestionsQuery,
  useGetFaqCategoryQuestionAnswerQuery,
  useGetFaqQuestionsSearchQuery,
  useGetCreatorRecentSearchQuery,
  useAddCreatorRecentSearchFaqMutation,
  useGetAllGrowthRecommendedProductQuery,
  useGetAllGrowthSimilarRecommendedProductQuery,
  useGetAllGrowthRealisedProductQuery,
  useLazyGetAllGrowthRealisedProductQuery,
  useOptinToGrowthRecommendationMutation,
  useCreateProductForGrowthSimilarProductRecommendationMutation,
  useFetchCreatorCollectionProductImageQuery,
  useGetCutoutsMutation,
  useLazyGetCutoutsByIdQuery,
  useDeleteCutoutsByIdMutation,
  useUploadCutoutsMutation,
  useGetCollageByIdQuery,
  useGetSingleCollageDataByIdQuery,
  useGetVariantForFeatureQuery,
  useLazyGetVariantForFeatureQuery,
  useLazyGetCreatorCampaignQuery,
  useGetCreatorCampaignQuery,
  useGetCreatorCampaignByIDQuery,
  useUpdateCampaignMutation,
  useTriggerContentSubmissionTemplateMutation,
  useLazyStartMultipartFileUploadQuery,
  useLazyGenerateMultipartPresignedUrlQuery,
  useCompleteMultipartUploadMutation,
  useLazyCreatorDeliverableFileUploadQuery,
  useSubmitDraftForReviewMutation,
  useUpdateCreatorFeedbackMutation,
  useMarkVariantVisitedMutation,
  useGetSuggestedActionsQuery,
  useDismissSuggestedActionMutation,
  useMarkCSMCallbackInitiatedMutation,
  useRearrangeProductsMutation,
  useGetAutoContentListQuery,
  useUploadVoiceNoteMutation,
  useLazyGetTDSCertificateQuery,
  useLazyGetTDSCertificateSummaryQuery,
  useLazyMailTDSCertificateQuery,
  useTriggerAutoConentCreationMutation,
  useFrontEndLoggerMutation,
  useGetBrandInterfaceDataMutation,
  useHandleEngageConnectionConfirmationMutation,
  useHandleFacebookEngageConfirmationMutation,
  useGetAllRewardsHistoryQuery,
  useLazyGetCollabProductsQuery,
  useAddToCartMutation,
  useGetCartQuery,
  useUpdateCartMutation,
  usePlaceOrderMutation,
  useVerifyPanMutation,
  useVerifyBankMutation,
  usePollPanStatusQuery,
  usePollBankStatusQuery,
  useVerifyRazorpayMutation,
  useShoppingAppFilterLabelsQuery,
  useFetchShoppingAppMetricsQuery,
  useLazyFetchShoppingAppMetricsQuery,
  usePollRazorpayStatusQuery,
  usePostAuthenticateOTPLessMutation,
  usePostFacebookLoginMutation,
  useConnectFacebookPageMutation,
  useGetPagesForFacebookLoginQuery,
  useGetCreatorFacebookEngageStatusQuery,
  useUpdateCreatorFacebookEngageSettingsMutation,
  useGetCreatorFacebookKeywordsQuery,
  useUpdateCreatorFacebookKeywordsMutation,
  useAddCreatorFacebookKeywordMutation,
  useGetCreatorFacebookTemplatesQuery,
  useAddCreatorFacebookTemplateMutation,
  useUpdateCreatorFacebookTemplateMutation,
  useRemoveCreatorFacebookTemplateMutation,
  useUpdateCreatorBrandAffiliationMutation,
  useGetCreatorBrandAffiliationQuery,
  useGetWhatsNewQuery,
  useGetWhatsNewCategoriesQuery,
  useGetWhatsNewDetailsQuery,
  useMarkWhatsNewAsReadMutation,
  useGetWhatsNewFeedbackQuery,
  useUpdateWhatsNewFeedbackMutation,
  useHandlePlotlineEventsDataMutation,
  useFtuxViewedMutation,
  useCreatorGSTDetailsQuery,
  useUploadGSTFileMutation,
  useCreateTDSInvoiceMutation,
  useLazyGetTDSInvoiceQuery,
  useClaimCreatorGSTMutation,
  useCreateGSTInvoiceMutation,
  useCreatorTDSInvoiceMailMutation,
  useMergeAndUploadInvoiceMutation,
  useGetCreatorTotalEarningsQuery,
  useLazyGetCreatorTotalEarningsQuery,
  useGetCreatorCollabHistoryQuery,
  useLazyGetCreatorCollabHistoryQuery,
  useCheckPostExistsMutation,
  useGetEngageSettingsPageViewQuery,
  useUpdateEngageSettingsPageViewMutation,
  useMarkEngageListingSeenMutation,
  useGetStoryBannerStatusQuery,
  useUpdateStoryBannerStatusMutation,
  useGetChatThreadQuery,
  useLazyGetChatThreadQuery,
  useGetScriptSubmissionsQuery,
  useLazyGetScriptSubmissionsQuery,
  usePostScriptSubmissionMutation,
  useGetThreadMessagesQuery,
  useLazyGetThreadMessagesQuery,
  useSendMessageMutation,
  useSubmitAdsCodeMutation,
  useChatMessageUpdateMutation,
  useGetChatUnreadMessageCountQuery,
  useUploadStoryScreenshotMutation,
  useGetCustomEngageMediaUploadUrlMutation,
  useGetChatReferenceLocationQuery,
  useLazyGetChatReferenceLocationQuery,
  useGetCreatorThreadUnreadMessageCountQuery,
  useLazyGetCreatorThreadUnreadMessageCountQuery,
  useGetIgStoryDrawerStatusQuery,
  useUpdateIgStoryDrawerStatusMutation,
  useOptCreatorInFeatureMutation,
  useMarkDiyEducationForcedViewMutation,
  // Folders
  useGetFoldersQuery,
  useLazyGetFoldersQuery,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useGetFolderItemsQuery,
  useLazyGetFolderItemsQuery,
  useAddLinkToFolderMutation,
  useAddLinkToFolderQuickMutation,
  useDeleteFolderItemMutation,
  useGetBrandRewardProductsQuery,
  useLazyGetBrandRewardProductsQuery,
  useGetBrandCollectionProductsQuery,
  useGetBrandRewardMaxMetricsQuery,
  useGetOffdutyRewardConstantQuery,
  useGetWishlinkConstantQuery,
  useGetBrandRewardProductWishlinkMutation,
  useEnableCustomEngageMutation,
  useEditProductMutation,
  useLazyGetOosReplacementsQuery,
  useSwapOosProductMutation,
  useGetEnhancedInstagramInsightsQuery,
  useGetAgentConversationsQuery,
  useLazyGetAgentConversationsQuery,
  useCreateAgentConversationMutation,
  useGetAgentConversationQuery,
  useLazyGetAgentConversationQuery,
  useGetAgentConversationMessagesQuery,
  useLazyGetAgentConversationMessagesQuery,
  useUpdateAgentConversationTitleMutation,
  useDeleteAgentConversationMutation,
  useGetBrandExperimentsForCreatorQuery,
  useGetBrandExperimentDetailForCreatorQuery,
  useGetBrandDetailForCreatorQuery,
  useGetBrandExperimentProductsForCreatorQuery,
  useGetCOCEventDataQuery,
  useGetContentInspirationV2Query,
  useGetContentInspirationV2RailQuery,
  useGetContentInspirationV2FiltersQuery,
  useGetDiscoverSearchAutosuggestQuery,
  useGetDiscoverSearchContentQuery,
  useGetDiscoverSearchFilterBrandsQuery,
  useGetDiscoverSearchFilterCategoriesQuery,
  useGetDiscoverSearchFilterGendersQuery,
  useGetDiscoverSearchFilterCountQuery,
  useGetDiscoverSearchDynamicFiltersQuery,
  useGetDiscoverSearchQuickFiltersQuery,
  useGetProductsFromPostQuery,
  useLazyGetProductsFromPostQuery,
  useGetCreatorDashboardSourceQuery,
  // IG Post Product Suggestions
  useGetIGPostSuggestionQuery,
  useApproveIGPostSuggestionMutation,
  useRejectIGPostSuggestionMutation,
  useGetDiyRewardModuleQuery,
  useGetSeleqtDiyCaptionNudgeQuery,
  useGetPostNudgesQuery,
  useGetCampaignStatsQuery,
} = reduxAPI;

export default reduxAPI;
